import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import {
  alpha,
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  CircularProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  DataObject,
  GraphicEq,
  HelpOutline,
  Info as InfoIcon,
  ListAlt,
  BugReport,
  Person,
  Troubleshoot as TroubleshootIcon,
  VoiceChat as VoiceChatIcon,
  Voicemail,
  FlagCircle,
  ExpandLess,
  ExpandMore,
  PlayArrow,
  Dialpad,
  Code,
  CheckCircleOutline,
  ErrorOutline,
  Schedule as ScheduleIcon,
  Pending as PendingIcon,
  PhoneInTalk,
  PhoneForwarded,
  RestartAlt,
} from '@mui/icons-material';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DiagnosticRecordingTracksViewer from './DiagnosticRecordingTracksViewer';
import WaveformPlayer, { type WaveformSeekRequest } from './WaveformPlayer';
import ButtonTabs, { type ButtonTab } from './ButtonTabs';
import CallControlCard from './CallControlCard';
import CallConsumptionDisplay from './CallConsumptionDisplay';
import { useCallLogs } from './call-detail/useCallLogs';
import { useCallRefunds } from './call-detail/useCallRefunds';
import { useCallScripts } from './call-detail/useCallScripts';
import { useTranslation } from 'react-i18next';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { formatPhone } from '../utils/phone';
import { formatDateTime } from '../utils/date';
import { formatTranscriptTimestamp } from '../utils/duration';
import type { CallHistoryItem, TranscriptTurn, CallLogItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { isSipOutboundSelfCall } from './call-detail/outboundSelf';
import { canExposeMonitorControl } from './call-detail/monitorControl';
import { getTerminationReasonDisplay } from './call-detail/terminationReasonDisplay';
import { getTransferStateSnapshot } from './call-detail/transferState';
import { buildCollectedDataEntries } from './call-detail/collectedData';
import {
  isCallTerminalForControlPanel,
  TERMINAL_CALL_STATUSES,
} from './call-detail/liveControlVisibility';

interface CallDetailPanelProps {
  call: CallHistoryItem;
  initialTab?: 'information' | 'general' | 'variables' | 'dataCollected' | 'scripts' | 'refunds' | 'diagnostics';
  controlMode?: 'default' | 'incoming' | 'outbound_self';
  canViewCostCurrency?: boolean;
  canViewCostCredits?: boolean;
  contactVariables?: Record<string, string>;
}

function getTurnTime(turn: TranscriptTurn) {
  const timeValue = turn.timeInCallSecs ?? turn.time_in_call_secs;
  if (timeValue === undefined || timeValue === null) return null;
  const parsed = Number(timeValue);
  return Number.isFinite(parsed) ? parsed : null;
}

function toTitleCase(value: string) {
  const spaced = value
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim();
  return spaced
    .split(/\s+/)
    .map((segment) => {
      if (!segment) return '';
      return `${segment[0].toUpperCase()}${segment.slice(1).toLowerCase()}`;
    })
    .join(' ');
}

type FinalizationDecisionState = 'applied' | 'skipped_duplicate' | 'unknown';

interface FinalizationDecisionSnapshot {
  decisionState: FinalizationDecisionState;
  finalStatus: string | null;
  reasonCode: string | null;
  source: string | null;
  sourceEventUid: string | null;
  idempotencyKey: string | null;
  updatedAt: string | null;
  sideEffectDoneCount: number;
  sideEffectSkippedDuplicateCount: number;
}

const FINALIZATION_CANONICAL_EVENTS = {
  APPLIED: 'call_finalization_applied',
  SKIPPED_TERMINAL: 'call_finalization_skipped_terminal',
} as const;

const FINALIZATION_SIDE_EFFECT_EVENTS = {
  DONE: 'call_finalization_side_effect_done',
  SKIPPED_DUPLICATE: 'call_finalization_side_effect_skipped_duplicate',
  LIFECYCLE_COMMAND_DONE: 'lifecycle_command_done',
} as const;

const CALL_DETAIL_SCOPE_CALL_ID = 'call';
const METRICS_SEGMENT_TYPE_AI = 'ai';

interface MetricsRow {
  label: string;
  value: React.ReactNode;
  color?: string;
}

interface MetricsValues {
  estimatedCostUsd: number | null;
  costPerMinuteUsd: number | null;
  llmPrice: number | null;
  sttPrice: number | null;
  ttsPrice: number | null;
  tier: string | null;
  costCredits: number | null;
  callCharge: number | null;
  llmCharge: number | null;
  avgResponseLatency: number | null;
  avgLlmLatency: number | null;
  avgTtsLatency: number | null;
  avgAsrLatency: number | null;
  maxResponseLatency: number | null;
  agentTurns: number | null;
  userTurns: number | null;
  userSilentTurns: number | null;
}

interface PercentileSnapshot {
  p50: number | null;
  p95: number | null;
  p99: number | null;
  count: number;
}

/**
 * Normalizes numeric payload values coming from API objects.
 *
 * Returns `null` for undefined/invalid values so reducers and averages can
 * ignore missing metrics without throwing.
 */
function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Sums numeric values and returns null when no valid value exists.
 */
function sumFiniteNumbers(values: Array<number | null>): number | null {
  let total = 0;
  let hasValue = false;
  values.forEach((value) => {
    if (value === null) return;
    total += value;
    hasValue = true;
  });
  return hasValue ? total : null;
}

/**
 * Calculates arithmetic mean for valid values only.
 */
function averageFiniteNumbers(values: Array<number | null>): number | null {
  const validValues = values.filter((value): value is number => value !== null);
  if (validValues.length === 0) return null;
  return validValues.reduce((total, value) => total + value, 0) / validValues.length;
}

/**
 * Calculates a weighted average with optional fallback to simple mean.
 *
 * When no valid positive weight is available, this method falls back to the
 * arithmetic mean of valid values to keep metrics resilient for older payloads.
 */
function weightedAverageFiniteNumbers(
  values: Array<number | null>,
  weights: Array<number | null>,
): number | null {
  let weightedTotal = 0;
  let weightTotal = 0;
  values.forEach((value, index) => {
    const weight = weights[index];
    if (value === null || weight === null || weight <= 0) return;
    weightedTotal += value * weight;
    weightTotal += weight;
  });
  if (weightTotal > 0) {
    return weightedTotal / weightTotal;
  }
  return averageFiniteNumbers(values);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function asString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value;
  return null;
}

function recordString(record: Record<string, unknown> | null, ...keys: string[]): string | null {
  if (!record) return null;
  for (const key of keys) {
    const value = asString(record[key]);
    if (value) return value;
  }
  return null;
}

function recordNumber(record: Record<string, unknown> | null, ...keys: string[]): number | null {
  if (!record) return null;
  for (const key of keys) {
    const value = toFiniteNumber(record[key]);
    if (value !== null) return value;
  }
  return null;
}

function percentile(values: number[], q: number): number | null {
  if (values.length === 0) return null;
  const ordered = [...values].sort((a, b) => a - b);
  if (ordered.length === 1) return ordered[0];
  const position = (ordered.length - 1) * q;
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  if (lower === upper) return ordered[lower];
  const lowerValue = ordered[lower];
  const upperValue = ordered[upper];
  return lowerValue + (upperValue - lowerValue) * (position - lower);
}

function buildPercentileSnapshot(values: number[]): PercentileSnapshot {
  return {
    count: values.length,
    p50: percentile(values, 0.5),
    p95: percentile(values, 0.95),
    p99: percentile(values, 0.99),
  };
}

/**
 * Extracts a resilient finalization snapshot from call logs.
 *
 * Primary source: canonical finalization events (`call_finalization_applied` /
 * `call_finalization_skipped_terminal`).
 *
 * Fallback source: side-effect events emitted by lifecycle runtime, used when
 * older or alternative flows finalize the call without canonical decision logs.
 */
function extractFinalizationDecision(logs: CallLogItem[], call: CallHistoryItem): FinalizationDecisionSnapshot | null {
  if (!Array.isArray(logs) || logs.length === 0) {
    return null;
  }

  let decisionState: FinalizationDecisionState = 'unknown';
  let finalStatus: string | null = null;
  let reasonCode: string | null = null;
  let source: string | null = null;
  let sourceEventUid: string | null = null;
  let idempotencyKey: string | null = null;
  let updatedAt: string | null = null;

  const logsDesc = [...logs].reverse();

  const canonicalDecisionLog = logsDesc.find(
    (log) => (
      log.event === FINALIZATION_CANONICAL_EVENTS.APPLIED
      || log.event === FINALIZATION_CANONICAL_EVENTS.SKIPPED_TERMINAL
    ),
  );
  if (canonicalDecisionLog) {
    const data = asRecord(canonicalDecisionLog.data);
    decisionState = canonicalDecisionLog.event === FINALIZATION_CANONICAL_EVENTS.SKIPPED_TERMINAL
      ? 'skipped_duplicate'
      : 'applied';
    finalStatus = recordString(data, 'finalStatus', 'final_status', 'status');
    reasonCode = recordString(data, 'reasonCode', 'reason_code', 'reason');
    source = recordString(data, 'source');
    sourceEventUid = recordString(data, 'sourceEventUid', 'source_event_uid', 'eventUid', 'event_uid');
    idempotencyKey = recordString(data, 'idempotencyKey', 'idempotency_key');
    updatedAt = canonicalDecisionLog.createdAt;
  }

  if (
    decisionState === 'unknown'
    && call.status
    && TERMINAL_CALL_STATUSES.has(call.status)
  ) {
    finalStatus = call.status;
  }

  const sideEffectDoneCount = logs.filter((log) => {
    if (log.event === FINALIZATION_SIDE_EFFECT_EVENTS.DONE) return true;
    if (log.event !== FINALIZATION_SIDE_EFFECT_EVENTS.LIFECYCLE_COMMAND_DONE) return false;
    const data = asRecord(log.data);
    const commandName = recordString(data, 'commandName', 'command_name');
    return commandName === 'HANGUP_INBOUND_ELEVENLABS' || commandName === 'SCHEDULE_FULL_RECORDING_UPLOAD';
  }).length;

  const sideEffectSkippedDuplicateCount = logs.filter(
    (log) => log.event === FINALIZATION_SIDE_EFFECT_EVENTS.SKIPPED_DUPLICATE,
  ).length;

  if (decisionState === 'unknown') {
    const latestSideEffectDoneLog = logsDesc.find(
      (log) => log.event === FINALIZATION_SIDE_EFFECT_EVENTS.DONE,
    );
    const latestSideEffectSkippedLog = logsDesc.find(
      (log) => log.event === FINALIZATION_SIDE_EFFECT_EVENTS.SKIPPED_DUPLICATE,
    );
    const sourceLog = latestSideEffectDoneLog ?? latestSideEffectSkippedLog;

    if (sideEffectDoneCount > 0) {
      decisionState = 'applied';
    } else if (sideEffectSkippedDuplicateCount > 0) {
      decisionState = 'skipped_duplicate';
    }

    if (sourceLog) {
      const sideEffectData = asRecord(sourceLog.data);
      const inferredReasonCode = recordString(sideEffectData, 'reasonCode', 'reason_code', 'reason');
      const inferredSource = recordString(sideEffectData, 'source');
      const inferredSourceEventUid = recordString(sideEffectData, 'sourceEventUid', 'source_event_uid', 'sourceEvent', 'source_event');
      const inferredIdempotencyKey = recordString(sideEffectData, 'idempotencyKey', 'idempotency_key');

      reasonCode = reasonCode ?? inferredReasonCode;
      source = source ?? inferredSource;
      sourceEventUid = sourceEventUid ?? inferredSourceEventUid;
      idempotencyKey = idempotencyKey ?? inferredIdempotencyKey;
      updatedAt = updatedAt ?? sourceLog.createdAt;
    }

  }

  if (
    decisionState === 'unknown'
    && !finalStatus
    && !reasonCode
    && sideEffectDoneCount === 0
    && sideEffectSkippedDuplicateCount === 0
  ) {
    return null;
  }

  return {
    decisionState,
    finalStatus,
    reasonCode,
    source,
    sourceEventUid,
    idempotencyKey,
    updatedAt,
    sideEffectDoneCount,
    sideEffectSkippedDuplicateCount,
  };
}

export default function CallDetailPanel({
  call,
  initialTab = 'information',
  controlMode = 'default',
  canViewCostCurrency = false,
  canViewCostCredits = false,
  contactVariables = {},
}: CallDetailPanelProps) {
  const { t } = useTranslation(['components', 'common', 'pages']);
  const { formatNumber, formatCurrency, formatDuration, formatLatency, locale } = useNumberFormat();
  const { user, isSuperuser, capabilities } = useAuth();
  const { hasPermission, groups } = usePermissions();
  const canViewRefunds = isSuperuser;
  const canViewDiagnostics = isSuperuser;
  const canViewAiSegmentRecording = isSuperuser || groups.some((group) => group.name === 'System Administrator');
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const scrollbarThumbColor = isDarkMode
    ? alpha(theme.palette.primary.light, 0.85)
    : theme.palette.primary.main;
  const scrollbarThumbHoverColor = isDarkMode
    ? theme.palette.primary.light
    : alpha(theme.palette.primary.dark, 0.8);
  const scrollbarTrackColor = isDarkMode
    ? alpha(theme.palette.background.paper, 0.4)
    : theme.palette.grey[100];

  const [playbackTime, setPlaybackTime] = useState<number | null>(null);
  const [seekRequest, setSeekRequest] = useState<WaveformSeekRequest | null>(null);
  const seekRequestIdRef = useRef(0);
  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [activeConversationSegmentId, setActiveConversationSegmentId] = useState<string>('');
  const [diagnosticsExpanded, setDiagnosticsExpanded] = useState(true);
  const [diagnosticsMetricsExpanded, setDiagnosticsMetricsExpanded] = useState(false);
  const [activeDiagnosticsTabId, setActiveDiagnosticsTabId] = useState<string>(CALL_DETAIL_SCOPE_CALL_ID);
  const [activeMetricsTabId, setActiveMetricsTabId] = useState<string>(CALL_DETAIL_SCOPE_CALL_ID);
  const [finalizationDecisionExpanded, setFinalizationDecisionExpanded] = useState(true);
  const [fullRecordingExpanded, setFullRecordingExpanded] = useState(true);
  const [segmentSummaryExpanded, setSegmentSummaryExpanded] = useState(true);
  const [segmentRecordingExpanded, setSegmentRecordingExpanded] = useState(true);
  const handleTranscriptSeek = (seconds: number) => {
    seekRequestIdRef.current += 1;
    setSeekRequest({
      time: seconds,
      play: true,
      requestId: seekRequestIdRef.current,
    });
  };

  const referenceAiSegment = useMemo(
    () => (call.segments ?? []).find((segment) => segment.segmentType === 'ai') ?? null,
    [call.segments],
  );
  const transcriptTurns = referenceAiSegment?.transcriptFull
    ? referenceAiSegment.transcriptFull.filter((turn) => turn.message && turn.message.trim() !== '')
    : [];
  const summaryText = referenceAiSegment?.conversationSummary
    || referenceAiSegment?.transcript
    || null;
  const dataEntries = useMemo(
    () => buildCollectedDataEntries(
      referenceAiSegment?.dataCollected,
      referenceAiSegment?.collectedVariables,
    ),
    [referenceAiSegment?.dataCollected, referenceAiSegment?.collectedVariables],
  );
  const hasDataEntries = dataEntries.length > 0;
  const recordingSource = call.recordingUrl ?? call.recordingDownloadUrl;
  const isAmdDiagnosticSegment = (segmentType?: string | null) =>
    segmentType === 'pregate' || segmentType === 'amd';
  const conversationSegments = useMemo(
    () => (
      (call.segments ?? []).filter((segment) => (
        canViewDiagnostics
          ? true
          : (segment.segmentType !== 'pregate' && segment.segmentType !== 'amd')
      ))
    ),
    [call.segments, canViewDiagnostics],
  );
  const selectedConversationSegmentId = useMemo(() => {
    if (conversationSegments.length === 0) return '';
    if (activeConversationSegmentId && conversationSegments.some((segment) => segment.id === activeConversationSegmentId)) {
      return activeConversationSegmentId;
    }
    const nonAmdDefault = conversationSegments.find(
      (segment) => segment.segmentType !== 'pregate' && segment.segmentType !== 'amd',
    );
    return (nonAmdDefault ?? conversationSegments[0]).id;
  }, [conversationSegments, activeConversationSegmentId]);
  const activeConversationSegment = useMemo(
    () => conversationSegments.find((segment) => segment.id === selectedConversationSegmentId) ?? conversationSegments[0] ?? null,
    [conversationSegments, selectedConversationSegmentId],
  );
  const diagnosticsSegments = useMemo(
    () => call.segments ?? [],
    [call.segments],
  );
  const segmentTranscriptTurns = activeConversationSegment?.transcriptFull
    ? activeConversationSegment.transcriptFull.filter((turn) => turn.message && turn.message.trim() !== '')
    : [];
  const isAiSegment = activeConversationSegment?.segmentType === 'ai';
  const selectedTranscriptTurns = isAmdDiagnosticSegment(activeConversationSegment?.segmentType)
    ? segmentTranscriptTurns
    : (
      isAiSegment
        ? (segmentTranscriptTurns.length > 0 ? segmentTranscriptTurns : transcriptTurns)
        : segmentTranscriptTurns
    );
  const selectedTranscriptTimes = selectedTranscriptTurns.map(getTurnTime);
  const selectedSummaryText = isAmdDiagnosticSegment(activeConversationSegment?.segmentType)
    ? (
      activeConversationSegment?.conversationSummary
      || activeConversationSegment?.transcript
    )
    : (
      isAiSegment
        ? (
          activeConversationSegment?.conversationSummary
          || activeConversationSegment?.transcript
          || summaryText
        )
        : (
          activeConversationSegment?.conversationSummary
          || activeConversationSegment?.transcript
        )
    );
  const selectedSegmentRecordingSource = (
    canViewAiSegmentRecording
      && activeConversationSegment?.segmentType === 'ai'
      && (
        activeConversationSegment.recordingDownloadUrl
        || activeConversationSegment.recordingUrl
      )
  ) || null;
  const getSegmentTabMeta = useCallback((segment: NonNullable<CallHistoryItem['segments']>[number]) => {
    if (segment.segmentType === 'ai') {
      return {
        title: t('components:callDetails.segmentTabs.aiTitle', 'Agente de IA'),
        tooltip: call.agentName
          ? t('components:callDetails.segmentTabs.aiTooltipWithAgent', 'Segmento do Agente de IA: {{agentName}}', {
            agentName: call.agentName,
          })
          : t('components:callDetails.segmentTabs.aiTooltip', 'Segmento de conversa com o agente de IA.'),
        icon: <PsychologyIcon sx={{ fontSize: 14 }} />,
      };
    }

    if (segment.segmentType === 'telephony_external_phone') {
      const destination = call.transferDestinationValue
        ? formatPhone(call.transferDestinationValue)
        : t('components:callDetails.segmentTabs.externalPhoneUnknown', 'número não informado');
      return {
        title: t('components:callConsumption.typesShort.telephony_external_phone', 'T-TE'),
        tooltip: t(
          'components:callDetails.segmentTabs.externalPhoneTooltip',
          'Transferência para telefone externo: {{phone}}',
          { phone: destination },
        ),
        icon: <PhoneForwarded sx={{ fontSize: 14 }} />,
      };
    }

    if (
      segment.segmentType === 'telephony_internal_extension'
      || segment.segmentType === 'telephony_outbound_internal_extension'
      || segment.segmentType === 'telephony_external_extension'
    ) {
      return {
        title: t(`components:callConsumption.types.${segment.segmentType}`, segment.segmentType),
        tooltip: t('components:callDetails.segmentTabs.extensionTooltip', 'Segmento de transferência para ramal.'),
        icon: <PhoneInTalk sx={{ fontSize: 14 }} />,
      };
    }

    if (segment.segmentType === 'pregate' || segment.segmentType === 'amd') {
      return {
        title: t(`components:callConsumption.typesShort.${segment.segmentType}`, segment.segmentType),
        tooltip: t('components:callDetails.segmentTabs.amdTooltip', 'Segmento técnico de triagem/AMD.'),
        icon: <Voicemail sx={{ fontSize: 14 }} />,
      };
    }

    if (segment.segmentType === 'ivr') {
      return {
        title: t('components:callConsumption.typesShort.ivr', 'IVR'),
        tooltip: t('components:callDetails.segmentTabs.ivrTooltip', 'Segmento de navegação em IVR.'),
        icon: <Dialpad sx={{ fontSize: 14 }} />,
      };
    }

    return {
      title: t('components:callDetails.segmentTabs.defaultTitle', 'Segmento'),
      tooltip: t('components:callDetails.segmentTabs.defaultTooltip', 'Segmento de chamada.'),
      icon: <VoiceChatIcon sx={{ fontSize: 14 }} />,
    };
  }, [call.agentName, call.transferDestinationValue, t]);
  const diagnosticsTabs = useMemo<ButtonTab[]>(() => ([
    {
      id: CALL_DETAIL_SCOPE_CALL_ID,
      label: t('components:callDetails.diagnosticsTabs.call', 'Call'),
    },
    ...diagnosticsSegments.map((segment) => ({
      id: segment.id,
      label: `${getSegmentTabMeta(segment).title} #${formatNumber(segment.sequence)}`,
    })),
  ]), [diagnosticsSegments, formatNumber, getSegmentTabMeta, t]);
  const selectedDiagnosticsTabId = useMemo(() => {
    if (activeDiagnosticsTabId === CALL_DETAIL_SCOPE_CALL_ID) {
      return CALL_DETAIL_SCOPE_CALL_ID;
    }
    return diagnosticsSegments.some((segment) => segment.id === activeDiagnosticsTabId)
      ? activeDiagnosticsTabId
      : CALL_DETAIL_SCOPE_CALL_ID;
  }, [activeDiagnosticsTabId, diagnosticsSegments]);
  const activeDiagnosticsSegment = useMemo(
    () => (
      selectedDiagnosticsTabId === CALL_DETAIL_SCOPE_CALL_ID
        ? null
        : diagnosticsSegments.find((segment) => segment.id === selectedDiagnosticsTabId) ?? null
    ),
    [diagnosticsSegments, selectedDiagnosticsTabId],
  );
  const aiMetricsSegments = useMemo(
    () => diagnosticsSegments.filter((segment) => segment.segmentType === METRICS_SEGMENT_TYPE_AI),
    [diagnosticsSegments],
  );
  const metricsTabs = useMemo<ButtonTab[]>(() => ([
    {
      id: CALL_DETAIL_SCOPE_CALL_ID,
      label: t('components:callDetails.metricsTabs.call', 'Call'),
    },
    ...aiMetricsSegments.map((segment) => ({
      id: segment.id,
      label: `${getSegmentTabMeta(segment).title} #${formatNumber(segment.sequence)}`,
    })),
  ]), [aiMetricsSegments, formatNumber, getSegmentTabMeta, t]);
  const selectedMetricsTabId = useMemo(() => {
    if (activeMetricsTabId === CALL_DETAIL_SCOPE_CALL_ID) {
      return CALL_DETAIL_SCOPE_CALL_ID;
    }
    return aiMetricsSegments.some((segment) => segment.id === activeMetricsTabId)
      ? activeMetricsTabId
      : CALL_DETAIL_SCOPE_CALL_ID;
  }, [activeMetricsTabId, aiMetricsSegments]);
  const activeMetricsSegment = useMemo(
    () => (
      selectedMetricsTabId === CALL_DETAIL_SCOPE_CALL_ID
        ? null
        : aiMetricsSegments.find((segment) => segment.id === selectedMetricsTabId) ?? null
    ),
    [aiMetricsSegments, selectedMetricsTabId],
  );
  const consolidatedMetrics = useMemo<MetricsValues | null>(() => {
    if (aiMetricsSegments.length === 0) return null;

    const estimatedCostUsd = sumFiniteNumbers(
      aiMetricsSegments.map((segment) => toFiniteNumber(segment.estimatedCostUsd)),
    );
    const totalDurationSeconds = sumFiniteNumbers(
      aiMetricsSegments.map((segment) => toFiniteNumber(segment.durationSeconds)),
    );
    const costPerMinuteUsd = (
      estimatedCostUsd !== null
      && totalDurationSeconds !== null
      && totalDurationSeconds > 0
    )
      ? estimatedCostUsd / (totalDurationSeconds / 60)
      : null;
    const tierValues = Array.from(new Set(
      aiMetricsSegments
        .map((segment) => segment.tier?.trim())
        .filter((value): value is string => Boolean(value)),
    ));
    const latencyWeights = aiMetricsSegments.map((segment) => toFiniteNumber(segment.agentTurns));
    const responseLatencyWeights = aiMetricsSegments.map((segment) => {
      const agentTurns = toFiniteNumber(segment.agentTurns) ?? 0;
      const userTurns = toFiniteNumber(segment.userTurns) ?? 0;
      const totalTurns = agentTurns + userTurns;
      return totalTurns > 0 ? totalTurns : null;
    });
    const maxResponseLatencyValues = aiMetricsSegments
      .map((segment) => toFiniteNumber(segment.maxResponseLatency))
      .filter((value): value is number => value !== null);

    return {
      estimatedCostUsd,
      costPerMinuteUsd,
      llmPrice: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.llmPrice))),
      sttPrice: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.sttPrice))),
      ttsPrice: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.ttsPrice))),
      tier: tierValues.length === 1
        ? tierValues[0]
        : (
          tierValues.length > 1
            ? t('components:callDetails.metricsMixedTier', 'Mixed')
            : null
        ),
      costCredits: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.costCredits))),
      callCharge: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.callCharge))),
      llmCharge: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.llmCharge))),
      avgResponseLatency: weightedAverageFiniteNumbers(
        aiMetricsSegments.map((segment) => toFiniteNumber(segment.avgResponseLatency)),
        responseLatencyWeights,
      ),
      avgLlmLatency: weightedAverageFiniteNumbers(
        aiMetricsSegments.map((segment) => toFiniteNumber(segment.avgLlmLatency)),
        latencyWeights,
      ),
      avgTtsLatency: weightedAverageFiniteNumbers(
        aiMetricsSegments.map((segment) => toFiniteNumber(segment.avgTtsLatency)),
        latencyWeights,
      ),
      avgAsrLatency: weightedAverageFiniteNumbers(
        aiMetricsSegments.map((segment) => toFiniteNumber(segment.avgAsrLatency)),
        latencyWeights,
      ),
      maxResponseLatency: maxResponseLatencyValues.length > 0 ? Math.max(...maxResponseLatencyValues) : null,
      agentTurns: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.agentTurns))),
      userTurns: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.userTurns))),
      userSilentTurns: sumFiniteNumbers(aiMetricsSegments.map((segment) => toFiniteNumber(segment.userSilentTurns))),
    };
  }, [aiMetricsSegments, t]);
  const selectedMetrics = useMemo<MetricsValues | null>(() => {
    if (selectedMetricsTabId === CALL_DETAIL_SCOPE_CALL_ID) {
      return consolidatedMetrics;
    }
    if (!activeMetricsSegment) {
      return null;
    }

    const estimatedCostUsd = toFiniteNumber(activeMetricsSegment.estimatedCostUsd);
    const durationSeconds = toFiniteNumber(activeMetricsSegment.durationSeconds);
    const costPerMinuteUsd = (
      estimatedCostUsd !== null
      && durationSeconds !== null
      && durationSeconds > 0
    )
      ? estimatedCostUsd / (durationSeconds / 60)
      : null;

    return {
      estimatedCostUsd,
      costPerMinuteUsd,
      llmPrice: toFiniteNumber(activeMetricsSegment.llmPrice),
      sttPrice: toFiniteNumber(activeMetricsSegment.sttPrice),
      ttsPrice: toFiniteNumber(activeMetricsSegment.ttsPrice),
      tier: activeMetricsSegment.tier ?? null,
      costCredits: toFiniteNumber(activeMetricsSegment.costCredits),
      callCharge: toFiniteNumber(activeMetricsSegment.callCharge),
      llmCharge: toFiniteNumber(activeMetricsSegment.llmCharge),
      avgResponseLatency: toFiniteNumber(activeMetricsSegment.avgResponseLatency),
      avgLlmLatency: toFiniteNumber(activeMetricsSegment.avgLlmLatency),
      avgTtsLatency: toFiniteNumber(activeMetricsSegment.avgTtsLatency),
      avgAsrLatency: toFiniteNumber(activeMetricsSegment.avgAsrLatency),
      maxResponseLatency: toFiniteNumber(activeMetricsSegment.maxResponseLatency),
      agentTurns: toFiniteNumber(activeMetricsSegment.agentTurns),
      userTurns: toFiniteNumber(activeMetricsSegment.userTurns),
      userSilentTurns: toFiniteNumber(activeMetricsSegment.userSilentTurns),
    };
  }, [activeMetricsSegment, consolidatedMetrics, selectedMetricsTabId]);
  const metricsRows = useMemo<MetricsRow[]>(() => {
    if (!selectedMetrics) return [];

    const rows: MetricsRow[] = [];
    if (canViewCostCurrency) {
      if (selectedMetrics.estimatedCostUsd !== null) {
        rows.push({
          label: t('components:callDetails.estimatedCost'),
          value: formatCurrency(selectedMetrics.estimatedCostUsd, 'USD'),
        });
      }
      if (selectedMetrics.costPerMinuteUsd !== null) {
        rows.push({
          label: t('components:callDetails.costPerMinute'),
          value: formatCurrency(selectedMetrics.costPerMinuteUsd, 'USD'),
        });
      }
      if (selectedMetrics.llmPrice !== null) {
        rows.push({
          label: t('components:callDetails.llmPrice'),
          value: formatCurrency(selectedMetrics.llmPrice, 'USD'),
        });
      }
      if (selectedMetrics.sttPrice !== null) {
        rows.push({
          label: t('components:callDetails.sttPrice'),
          value: formatCurrency(selectedMetrics.sttPrice, 'USD'),
        });
      }
      if (selectedMetrics.ttsPrice !== null) {
        rows.push({
          label: t('components:callDetails.ttsPrice'),
          value: formatCurrency(selectedMetrics.ttsPrice, 'USD'),
        });
      }
      if (selectedMetrics.tier) {
        rows.push({
          label: t('components:callDetails.tier'),
          value: selectedMetrics.tier,
        });
      }
    }
    if (canViewCostCredits && selectedMetrics.costCredits !== null) {
      rows.push({
        label: t('components:callDetails.creditsUsed'),
        value: formatNumber(selectedMetrics.costCredits),
      });
    }
    if (canViewCostCredits && selectedMetrics.callCharge !== null) {
      rows.push({
        label: t('components:callDetails.callChargeCredits'),
        value: formatNumber(selectedMetrics.callCharge),
      });
    }
    if (canViewCostCredits && selectedMetrics.llmCharge !== null) {
      rows.push({
        label: t('components:callDetails.llmChargeCredits'),
        value: formatNumber(selectedMetrics.llmCharge),
      });
    }
    if (selectedMetrics.avgResponseLatency !== null) {
      rows.push({
        label: t('components:callDetails.avgResponseLatency'),
        value: `${formatLatency(selectedMetrics.avgResponseLatency, true)}`,
        color: selectedMetrics.avgResponseLatency > 1.5
          ? 'error.main'
          : (selectedMetrics.avgResponseLatency > 1.0 ? 'warning.main' : 'success.main'),
      });
    }
    if (selectedMetrics.avgLlmLatency !== null) {
      rows.push({
        label: t('components:callDetails.avgLlmLatency'),
        value: `${formatLatency(selectedMetrics.avgLlmLatency, true)}`,
      });
    }
    if (selectedMetrics.avgTtsLatency !== null) {
      rows.push({
        label: t('components:callDetails.avgTtsLatency'),
        value: `${formatLatency(selectedMetrics.avgTtsLatency, true)}`,
      });
    }
    if (selectedMetrics.avgAsrLatency !== null) {
      rows.push({
        label: t('components:callDetails.avgAsrLatency'),
        value: `${formatLatency(selectedMetrics.avgAsrLatency, true)}`,
      });
    }
    if (selectedMetrics.maxResponseLatency !== null) {
      rows.push({
        label: t('components:callDetails.maxResponseLatency', 'Max Response Latency:'),
        value: `${formatLatency(selectedMetrics.maxResponseLatency, true)}`,
      });
    }
    if (selectedMetrics.agentTurns !== null) {
      rows.push({
        label: t('components:callDetails.agentTurns'),
        value: formatNumber(selectedMetrics.agentTurns),
      });
    }
    if (selectedMetrics.userTurns !== null) {
      rows.push({
        label: t('components:callDetails.userTurns'),
        value: formatNumber(selectedMetrics.userTurns),
      });
    }
    if (selectedMetrics.userSilentTurns !== null) {
      rows.push({
        label: t('components:callDetails.silentTurns'),
        value: formatNumber(selectedMetrics.userSilentTurns),
      });
    }
    return rows;
  }, [canViewCostCredits, canViewCostCurrency, formatCurrency, formatLatency, formatNumber, selectedMetrics, t]);
  const hasAnyRecording = Boolean(
    recordingSource
      || (
        canViewAiSegmentRecording
        && (call.segments ?? []).some(
          (segment) => segment.segmentType === 'ai' && Boolean(segment.recordingUrl || segment.recordingDownloadUrl),
        )
      ),
  );
  const {
    logs,
    loading: logsLoading,
    error: logsError,
  } = useCallLogs({
    callId: call.id,
    enabled: activeTab === 'diagnostics' && canViewDiagnostics,
  });
  const billingOverageLog = logs.find((log) => log.event === 'billing_overage_applied');

  const formatTimestampWithMicros = useCallback((value?: string) => {
    if (!value) return '-';
    const formatted = formatDateTime(value, {
      locale,
      fallback: value,
      options: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      },
    });

    // Backend timestamps often carry microseconds (6 digits). Intl DateTimeFormat
    // only supports milliseconds at best, so we preserve the fraction from the
    // original ISO string.
    const match = String(value).match(/\.(\d{1,9})(?:Z|[+-]\d{2}:\d{2})?$/);
    const fraction = match ? match[1].slice(0, 6).padEnd(6, '0') : '000000';
    return `${formatted}.${fraction}`;
  }, [locale]);
  const billingOverageSeconds = typeof billingOverageLog?.data?.['overage_seconds'] === 'number'
    ? Number(billingOverageLog.data?.['overage_seconds'])
    : null;
  const finalizationDecision = useMemo(
    () => extractFinalizationDecision(logs, call),
    [logs, call],
  );
  const operationalDiagnostics = useMemo(() => {
    const metricLogs = logs.filter((log) => log.event === 'lifecycle_event_metrics');
    const hangupForwardSamples: number[] = [];
    const monitorStopSamples: number[] = [];
    const inboundBridgeSamples: number[] = [];
    const pstnAmdSamples: number[] = [];
    let terminalDuplicateSkipsTotal = 0;
    let terminalRedundantExecTotal = 0;
    let ingressDuplicateSkipsTotal = 0;

    metricLogs.forEach((log) => {
      const dataRecord = asRecord(log.data);
      const phase11Record = asRecord(
        (dataRecord?.phase11Metrics ?? dataRecord?.phase11_metrics) as unknown,
      );
      if (!phase11Record) return;

      const terminalHangup = recordNumber(phase11Record, 'terminalEventToHangupForwardMs', 'terminal_event_to_hangup_forward_ms');
      const terminalMonitorStop = recordNumber(phase11Record, 'terminalEventToMonitorStopMs', 'terminal_event_to_monitor_stop_ms');
      const inboundBridge = recordNumber(phase11Record, 'inboundArrivalToBridgeAttemptMs', 'inbound_arrival_to_bridge_attempt_ms');
      const pstnAmd = recordNumber(phase11Record, 'pstnAnsweredToAmdResultMs', 'pstn_answered_to_amd_result_ms');
      const terminalDuplicateSkips = recordNumber(
        phase11Record,
        'terminalSideEffectDuplicateSkipsTotal',
        'terminal_side_effect_duplicate_skips_total',
      );
      const terminalRedundantExec = recordNumber(
        phase11Record,
        'terminalSideEffectRedundantExecTotal',
        'terminal_side_effect_redundant_exec_total',
      );
      const ingressDuplicateSkips = recordNumber(
        phase11Record,
        'ingressCommandDuplicateSkipsTotal',
        'ingress_command_duplicate_skips_total',
      );

      if (terminalHangup !== null && terminalHangup >= 0) hangupForwardSamples.push(terminalHangup);
      if (terminalMonitorStop !== null && terminalMonitorStop >= 0) monitorStopSamples.push(terminalMonitorStop);
      if (inboundBridge !== null && inboundBridge >= 0) inboundBridgeSamples.push(inboundBridge);
      if (pstnAmd !== null && pstnAmd >= 0) pstnAmdSamples.push(pstnAmd);
      terminalDuplicateSkipsTotal += terminalDuplicateSkips ?? 0;
      terminalRedundantExecTotal += terminalRedundantExec ?? 0;
      ingressDuplicateSkipsTotal += ingressDuplicateSkips ?? 0;
    });

    return {
      hasData: metricLogs.length > 0,
      eventsCount: metricLogs.length,
      hangupForward: buildPercentileSnapshot(hangupForwardSamples),
      monitorStop: buildPercentileSnapshot(monitorStopSamples),
      inboundBridge: buildPercentileSnapshot(inboundBridgeSamples),
      pstnAmd: buildPercentileSnapshot(pstnAmdSamples),
      terminalDuplicateSkipsTotal,
      terminalRedundantExecTotal,
      ingressDuplicateSkipsTotal,
    };
  }, [logs]);
  const [expandedLogId, setExpandedLogId] = useState<string | number | null>(null);
  const [expandedExecutionId, setExpandedExecutionId] = useState<string | null>(null);
  const [refundReason, setRefundReason] = useState('');
  const canControlMonitor = canExposeMonitorControl({
    call,
    controlMode,
    hasMonitorPermission: hasPermission('calls_control_monitor'),
    userId: user?.id ?? null,
    userExtension: user?.telephonyEndpoint?.extension ?? null,
  });
  const canControlHangup = hasPermission('calls_control_hangup');
  const canControlHoldOrMute = hasPermission('calls_control_put_on_hold');
  const canControlHold = canControlHoldOrMute && Boolean(capabilities?.hasAgentHoldMusic);
  const canExecuteScripts = isSuperuser || hasPermission('scripts_execute');
  const showControlPanel = canControlMonitor || canControlHangup || canControlHoldOrMute;
  const formatOperationalLatency = (value: number | null) => (
    value === null
      ? '-'
      : `${formatNumber(Math.round(value))} ms`
  );
  const {
    data: refundsData,
    loading: refundsLoading,
    error: refundsError,
    executeState: refundExecuteState,
    executeError: refundExecuteError,
    executeRefund,
  } = useCallRefunds({
    callId: call.id,
    enabled: activeTab === 'refunds' && canViewRefunds,
  });
  const {
    callScripts,
    scriptExecutions,
    scriptsLoading,
    scriptsError,
    selectedScriptId,
    executeState,
    executeError,
    setSelectedScriptId,
    executeScript,
  } = useCallScripts({
    callId: call.id,
    enabled: activeTab === 'scripts',
  });
  const formatNumberValue = (value?: number | null) =>
    value === null || value === undefined ? '-' : formatNumber(value);
  const formatDateValue = (value?: string | null) =>
    value ? formatDateTime(value, { locale }) : '-';
  const statusLabel = call.status
    ? t(`components:statusChips.call.labels.${call.status}`, call.status)
    : '-';
  const destinationMetadataItems = useMemo(() => {
    const items: string[] = [];
    const phoneType = String(call.toNumberPhoneType || '').trim();
    const areaCode = String(call.toNumberAreaCode || '').trim();
    const stateCode = String(call.toNumberStateCode || '').trim();
    const stateName = String(call.toNumberStateName || '').trim();
    const regionCode = String(call.toNumberRegionCode || '').trim();
    const regionName = String(call.toNumberRegionName || '').trim();
    const timezoneName = String(call.toNumberTimezone || '').trim();
    const carrierName = String(call.toNumberCarrierCanonicalName || '').trim();
    const carrierSource = String(call.toNumberCarrierSource || '').trim();

    if (phoneType) {
      items.push(
        t(`pages:calls.filters.phoneTypeOptions.${phoneType}`, toTitleCase(phoneType)),
      );
    }
    if (areaCode) {
      items.push(`${t('pages:calls.filters.dddLabel', 'DDD')} ${areaCode}`);
    }
    if (stateCode || stateName) {
      if (stateCode && stateName) {
        items.push(`${stateName} (${stateCode})`);
      } else {
        items.push(stateName || stateCode);
      }
    }
    if (regionCode || regionName) {
      items.push(
        regionCode
          ? t(`pages:calls.filters.regionOptions.${regionCode}`, regionName || toTitleCase(regionCode))
          : regionName,
      );
    }
    if (timezoneName) {
      items.push(timezoneName);
    }
    if (carrierName || carrierSource === 'unknown') {
      items.push(
        `${t('components:callDetails.carrier', 'Carrier')}: ${
          carrierName || t('components:callDetails.unidentifiedCarrier', 'Unidentified carrier')
        }`,
      );
    }
    return items;
  }, [
    call.toNumberAreaCode,
    call.toNumberCarrierCanonicalName,
    call.toNumberCarrierSource,
    call.toNumberPhoneType,
    call.toNumberRegionCode,
    call.toNumberRegionName,
    call.toNumberStateCode,
    call.toNumberStateName,
    call.toNumberTimezone,
    t,
  ]);
  const latestIvrSegment = useMemo(() => {
    const ivrSegments = (call.segments ?? []).filter((segment) => segment.segmentType === 'ivr');
    if (ivrSegments.length === 0) return null;
    return ivrSegments.reduce((latest, segment) => (
      (segment.sequence ?? 0) >= (latest.sequence ?? 0) ? segment : latest
    ));
  }, [call.segments]);
  const formatIvrStatusLabel = (value?: string | null) => {
    if (!value) return '-';
    if (value === 'converted' || value === 'optout' || value === 'mismatch') {
      return t(`components:statusChips.callOutcome.labels.${value}`, value);
    }
    return toTitleCase(value);
  };
  const ivrSelectedDigit = call.ivrSelectedDigit || latestIvrSegment?.ivrDtmfDigit || '-';
  const ivrSelectedStatus = call.ivrSelectedStatus || latestIvrSegment?.ivrDtmfMappedStatus || null;
  const ivrResolution = call.ivrResolution || null;
  const hasIvrSummary = Boolean(
    call.ivrSelectedDigit
      || call.ivrSelectedStatus
      || call.ivrResolution
      || latestIvrSegment,
  );
  const terminationReasonDisplay = useMemo(() => {
    return getTerminationReasonDisplay(t, call.terminationReasonCode, call.terminationReasonText);
  }, [call.terminationReasonCode, call.terminationReasonText, t]);
  const terminationOriginDisplay = useMemo(() => {
    const origin = String(call.terminationOrigin || '').trim();
    if (!origin) {
      return '-';
    }
    return t(
      `components:callDetails.terminationOrigins.${origin}`,
      toTitleCase(origin),
    );
  }, [call.terminationOrigin, t]);
  const callDiagnosticsFields = [
    { key: 'id', label: t('components:callDetails.diagnosticsFields.id', 'id'), value: call.id || '-' },
    { key: 'callSid', label: t('components:callDetails.diagnosticsFields.callSid', 'call_sid'), value: call.callSid || '-' },
    { key: 'sipCallId', label: t('components:callDetails.diagnosticsFields.sipCallId', 'sip_call_id'), value: call.sipCallId || '-' },
    { key: 'status', label: t('components:callDetails.diagnosticsFields.status', 'status'), value: statusLabel },
    {
      key: 'ivrSelectedDigit',
      label: t('components:callDetails.diagnosticsFields.ivrSelectedDigit', 'ivr_selected_digit'),
      value: ivrSelectedDigit,
    },
    {
      key: 'ivrSelectedStatus',
      label: t('components:callDetails.diagnosticsFields.ivrSelectedStatus', 'ivr_selected_status'),
      value: formatIvrStatusLabel(ivrSelectedStatus),
    },
    {
      key: 'ivrResolution',
      label: t('components:callDetails.diagnosticsFields.ivrResolution', 'ivr_resolution'),
      value: ivrResolution || '-',
    },
    { key: 'originSipIp', label: t('components:callDetails.diagnosticsFields.originSipIp', 'origin_sip_ip'), value: call.originSipIp || '-' },
    { key: 'originSipPort', label: t('components:callDetails.diagnosticsFields.originSipPort', 'origin_sip_port'), value: formatNumberValue(call.originSipPort) },
    { key: 'asteriskChannelId', label: t('components:callDetails.diagnosticsFields.asteriskChannelId', 'asterisk_channel_id'), value: call.asteriskChannelId || '-' },
    { key: 'asteriskUniqueid', label: t('components:callDetails.diagnosticsFields.asteriskUniqueid', 'asterisk_uniqueid'), value: call.asteriskUniqueid || '-' },
    { key: 'asteriskLinkedid', label: t('components:callDetails.diagnosticsFields.asteriskLinkedid', 'asterisk_linkedid'), value: call.asteriskLinkedid || '-' },
    { key: 'queuedAt', label: t('components:callDetails.diagnosticsFields.queuedAt', 'queued_at'), value: formatDateValue(call.queuedAt) },
    { key: 'initiatedAt', label: t('components:callDetails.diagnosticsFields.initiatedAt', 'initiated_at'), value: formatDateValue(call.initiatedAt) },
    { key: 'ringingAt', label: t('components:callDetails.diagnosticsFields.ringingAt', 'ringing_at'), value: formatDateValue(call.ringingAt) },
    { key: 'answeredAt', label: t('components:callDetails.diagnosticsFields.answeredAt', 'answered_at'), value: formatDateValue(call.answeredAt) },
    { key: 'endedAt', label: t('components:callDetails.diagnosticsFields.endedAt', 'ended_at'), value: formatDateValue(call.endedAt) },
    { key: 'ringDuration', label: t('components:callDetails.diagnosticsFields.ringDuration', 'ring_duration'), value: formatNumberValue(call.ringDuration) },
    {
      key: 'terminationReasonCode',
      label: t('components:callDetails.diagnosticsFields.terminationReasonCode', 'termination_reason_code'),
      value: call.terminationReasonCode || '-',
    },
    {
      key: 'terminationReasonText',
      label: t('components:callDetails.diagnosticsFields.terminationReasonText', 'termination_reason_text'),
      value: call.terminationReasonText || '-',
    },
    {
      key: 'terminationOrigin',
      label: t('components:callDetails.diagnosticsFields.terminationOrigin', 'termination_origin'),
      value: terminationOriginDisplay,
    },
    { key: 'errorMessage', label: t('components:callDetails.diagnosticsFields.errorMessage', 'error_message'), value: call.errorMessage || '-' },
  ];
  const activeSegmentAnalysis = asRecord(activeDiagnosticsSegment?.analysis);
  const activeSegmentVoicemail = asRecord(activeDiagnosticsSegment?.voicemailDetection);
  const activeSegmentAmdStatus = (
    asString(activeSegmentAnalysis?.asterisk_amd_status)
    || asString(activeSegmentAnalysis?.amd_status)
    || asString(activeSegmentAnalysis?.status)
    || asString(activeSegmentVoicemail?.status)
    || asString(activeSegmentVoicemail?.effective_status)
    || asString(asRecord(activeSegmentVoicemail?.amd)?.status)
    || asString(asRecord(activeSegmentVoicemail?.amd)?.effective_status)
  );
  const activeSegmentAmdCause = (
    asString(activeSegmentAnalysis?.asterisk_amd_cause)
    || asString(activeSegmentAnalysis?.amd_cause)
    || asString(activeSegmentAnalysis?.cause)
    || asString(activeSegmentVoicemail?.cause)
    || asString(asRecord(activeSegmentVoicemail?.amd)?.cause)
  );
  const activeSegmentDiagnosticsFields = activeDiagnosticsSegment
    ? [
      {
        key: 'segmentType',
        label: t('components:callDetails.diagnosticsFields.segmentType', 'segment_type'),
        value: getSegmentTabMeta(activeDiagnosticsSegment).title,
      },
      {
        key: 'sequence',
        label: t('components:callDetails.diagnosticsFields.sequence', 'sequence'),
        value: formatNumberValue(activeDiagnosticsSegment.sequence),
      },
      {
        key: 'sourceChannelId',
        label: t('components:callDetails.diagnosticsFields.sourceChannelId', 'source_channel_id'),
        value: activeDiagnosticsSegment.sourceChannelId || '-',
      },
      {
        key: 'sourceUniqueid',
        label: t('components:callDetails.diagnosticsFields.sourceUniqueid', 'source_uniqueid'),
        value: activeDiagnosticsSegment.sourceUniqueid || '-',
      },
      {
        key: 'sourceLinkedid',
        label: t('components:callDetails.diagnosticsFields.sourceLinkedid', 'linkedid'),
        value: activeDiagnosticsSegment.linkedid || '-',
      },
      {
        key: 'segmentStartedAt',
        label: t('components:callDetails.diagnosticsFields.segmentStartedAt', 'started_at'),
        value: formatDateValue(activeDiagnosticsSegment.startedAt),
      },
      {
        key: 'segmentConnectedAt',
        label: t('components:callDetails.diagnosticsFields.segmentConnectedAt', 'connected_at'),
        value: formatDateValue(activeDiagnosticsSegment.connectedAt),
      },
      {
        key: 'segmentEndedAt',
        label: t('components:callDetails.diagnosticsFields.segmentEndedAt', 'ended_at'),
        value: formatDateValue(activeDiagnosticsSegment.endedAt),
      },
      {
        key: 'segmentDurationSeconds',
        label: t('components:callDetails.diagnosticsFields.segmentDurationSeconds', 'duration_seconds'),
        value: formatNumberValue(activeDiagnosticsSegment.durationSeconds),
      },
      {
        key: 'segmentBilledSeconds',
        label: t('components:callDetails.diagnosticsFields.segmentBilledSeconds', 'billed_seconds'),
        value: formatNumberValue(activeDiagnosticsSegment.billedSeconds),
      },
      {
        key: 'segmentAmdStatus',
        label: t('components:callDetails.diagnosticsFields.segmentAmdStatus', 'amd_status'),
        value: activeSegmentAmdStatus || '-',
      },
      {
        key: 'segmentAmdCause',
        label: t('components:callDetails.diagnosticsFields.segmentAmdCause', 'amd_cause'),
        value: activeSegmentAmdCause || '-',
      },
      {
        key: 'segmentIvrDigit',
        label: t('components:callDetails.diagnosticsFields.segmentIvrDigit', 'ivr_dtmf_digit'),
        value: activeDiagnosticsSegment.ivrDtmfDigit || '-',
      },
      {
        key: 'segmentIvrMappedStatus',
        label: t('components:callDetails.diagnosticsFields.segmentIvrMappedStatus', 'ivr_dtmf_mapped_status'),
        value: formatIvrStatusLabel(activeDiagnosticsSegment.ivrDtmfMappedStatus),
      },
      {
        key: 'segmentIvrEndReason',
        label: t('components:callDetails.diagnosticsFields.segmentIvrEndReason', 'ivr_end_reason'),
        value: activeDiagnosticsSegment.ivrEndReason || '-',
      },
    ]
    : [];
  const activeSegmentAnalysisPayload = activeDiagnosticsSegment?.analysis
    ? JSON.stringify(activeDiagnosticsSegment.analysis, null, 2)
    : null;
  const activeSegmentVoicemailPayload = activeDiagnosticsSegment?.voicemailDetection
    ? JSON.stringify(activeDiagnosticsSegment.voicemailDetection, null, 2)
    : null;

  const diagnosticsContent = canViewDiagnostics ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%', minHeight: 0 }}>
      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            cursor: 'pointer',
          }}
          onClick={() => setFinalizationDecisionExpanded((prev) => !prev)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <CheckCircleOutline fontSize="small" color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {t('components:callDetails.finalizationDecision.title', 'Finalization decision')}
            </Typography>
            <Tooltip title={t('components:callDetails.finalizationDecision.help', 'Canonical finalization snapshot for this call.')} arrow>
              <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
            {finalizationDecision && (
              <Chip
                size="small"
                color={finalizationDecision.decisionState === 'applied' ? 'success' : 'warning'}
                label={t(
                  `components:callDetails.finalizationDecision.states.${finalizationDecision.decisionState}`,
                  finalizationDecision.decisionState,
                )}
                sx={{ fontWeight: 700 }}
              />
            )}
          </Box>
          {finalizationDecisionExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Box>
        <Collapse in={finalizationDecisionExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
            {finalizationDecision ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                <Typography variant="body2">
                  <strong>{t('components:callDetails.finalizationDecision.fields.finalStatus', 'Final status')}:</strong>{' '}
                  {finalizationDecision.finalStatus
                    ? t(`components:statusChips.call.labels.${finalizationDecision.finalStatus}`, finalizationDecision.finalStatus)
                    : '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('components:callDetails.finalizationDecision.fields.reasonCode', 'Reason code')}:</strong>{' '}
                  {finalizationDecision.reasonCode || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('components:callDetails.finalizationDecision.fields.source', 'Source')}:</strong>{' '}
                  {finalizationDecision.source || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('components:callDetails.finalizationDecision.fields.sourceEventUid', 'Source event UID')}:</strong>{' '}
                  {finalizationDecision.sourceEventUid || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('components:callDetails.finalizationDecision.fields.idempotencyKey', 'Idempotency key')}:</strong>{' '}
                  {finalizationDecision.idempotencyKey || '-'}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('components:callDetails.finalizationDecision.fields.sideEffectDoneCount', 'Side effects done')}:</strong>{' '}
                  {formatNumber(finalizationDecision.sideEffectDoneCount)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('components:callDetails.finalizationDecision.fields.sideEffectSkippedCount', 'Side effects skipped (duplicate)')}:</strong>{' '}
                  {formatNumber(finalizationDecision.sideEffectSkippedDuplicateCount)}
                </Typography>
                <Typography variant="body2">
                  <strong>{t('components:callDetails.finalizationDecision.fields.updatedAt', 'Updated at')}:</strong>{' '}
                  {formatDateValue(finalizationDecision.updatedAt)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('components:callDetails.finalizationDecision.empty', 'No finalization decision snapshot available yet.')}
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setDiagnosticsExpanded((prev) => !prev)}
        >
          <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BugReport fontSize="small" color="primary" />
            {t('components:callDetails.diagnostics', 'Diagnostico')}
          </Typography>
          {diagnosticsExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Box>
        <Collapse in={diagnosticsExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ButtonTabs
                tabs={diagnosticsTabs}
                value={selectedDiagnosticsTabId}
                onChange={(_, value) => setActiveDiagnosticsTabId(value)}
                renderContent={false}
                tabsSx={{ mt: 0, '& .MuiTabs-flexContainer': { gap: 1 } }}
              />

              {selectedDiagnosticsTabId === CALL_DETAIL_SCOPE_CALL_ID ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                  {callDiagnosticsFields.map((field) => (
                    <Typography key={field.key} variant="body2">
                      <strong>{field.label}:</strong> {field.value}
                    </Typography>
                  ))}
                </Box>
              ) : activeDiagnosticsSegment ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                    {activeSegmentDiagnosticsFields.map((field) => (
                      <Typography key={field.key} variant="body2">
                        <strong>{field.label}:</strong> {field.value}
                      </Typography>
                    ))}
                  </Box>
                  {activeSegmentAnalysisPayload && (
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                        {t('components:callDetails.diagnosticsFields.segmentAnalysis', 'Segment analysis')}
                      </Typography>
                      <Box sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            m: 0,
                            fontFamily: 'Monospace',
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {activeSegmentAnalysisPayload}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {activeSegmentVoicemailPayload && (
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                        {t('components:callDetails.diagnosticsFields.segmentVoicemail', 'Segment voicemail')}
                      </Typography>
                      <Box sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                        <Typography
                          variant="body2"
                          component="pre"
                          sx={{
                            m: 0,
                            fontFamily: 'Monospace',
                            fontSize: 12,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {activeSegmentVoicemailPayload}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('components:callDetails.diagnosticsNoSegments', 'No segment diagnostics available.')}
                </Typography>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setDiagnosticsMetricsExpanded((prev) => !prev)}
        >
          <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TroubleshootIcon fontSize="small" color="primary" />
            {t('components:callDetails.metricsTitle', 'Metrics and Costs')}
          </Typography>
          {diagnosticsMetricsExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Box>
        <Collapse in={diagnosticsMetricsExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <ButtonTabs
                tabs={metricsTabs}
                value={selectedMetricsTabId}
                onChange={(_, value) => setActiveMetricsTabId(value)}
                renderContent={false}
                tabsSx={{ mt: 0, '& .MuiTabs-flexContainer': { gap: 1 } }}
              />

              {metricsRows.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {metricsRows.map((entry) => (
                    <Box
                      key={entry.label}
                      sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {entry.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color={entry.color || 'text.primary'}
                      >
                        {entry.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('components:callDetails.metricsEmpty', 'No metrics available.')}
                </Typography>
              )}
            </Box>
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TroubleshootIcon fontSize="small" color="primary" />
          {t('components:callDetails.operationalDiagnostics.title', 'Operational diagnostics (Phase 11)')}
        </Typography>
        {operationalDiagnostics.hasData ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.eventsCount', 'Lifecycle metric events')}:</strong>{' '}
              {formatNumber(operationalDiagnostics.eventsCount)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.terminalDuplicateSkips', 'Terminal duplicate skips')}:</strong>{' '}
              {formatNumber(operationalDiagnostics.terminalDuplicateSkipsTotal)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.terminalRedundantExec', 'Terminal redundant exec')}:</strong>{' '}
              {formatNumber(operationalDiagnostics.terminalRedundantExecTotal)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.ingressDuplicateSkips', 'Ingress duplicate skips')}:</strong>{' '}
              {formatNumber(operationalDiagnostics.ingressDuplicateSkipsTotal)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.hangupForwardP95', 'Hangup forward p95')}:</strong>{' '}
              {formatOperationalLatency(operationalDiagnostics.hangupForward.p95)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.monitorStopP95', 'Monitor stop p95')}:</strong>{' '}
              {formatOperationalLatency(operationalDiagnostics.monitorStop.p95)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.inboundBridgeP95', 'Inbound to bridge p95')}:</strong>{' '}
              {formatOperationalLatency(operationalDiagnostics.inboundBridge.p95)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.pstnAmdP95', 'PSTN answered to AMD p95')}:</strong>{' '}
              {formatOperationalLatency(operationalDiagnostics.pstnAmd.p95)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.hangupForwardP99', 'Hangup forward p99')}:</strong>{' '}
              {formatOperationalLatency(operationalDiagnostics.hangupForward.p99)}
            </Typography>
            <Typography variant="body2">
              <strong>{t('components:callDetails.operationalDiagnostics.monitorStopP99', 'Monitor stop p99')}:</strong>{' '}
              {formatOperationalLatency(operationalDiagnostics.monitorStop.p99)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('components:callDetails.operationalDiagnostics.empty', 'No phase 11 lifecycle diagnostics available yet.')}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ListAlt fontSize="small" color="primary" />
          {t('components:callDetails.logs', 'Logs')}
        </Typography>
        {logsLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('components:callDetails.logsLoading', 'Loading logs...')}
            </Typography>
          </Box>
        ) : logsError ? (
          <Alert severity="error" variant="outlined">
            {logsError}
          </Alert>
        ) : logs.length > 0 ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {logs.map((log, logIndex) => {
              const logIdentifier = log.id ?? logIndex;
              const isExpanded = expandedLogId === logIdentifier;
              const level = log.level ?? 'info';
              const logLevelConfig = (() => {
                switch (level) {
                  case 'error':
                    return {
                      icon: <TroubleshootIcon fontSize="small" sx={{ color: 'error.main' }} />,
                      textColor: 'error.main',
                      borderColor: alpha(theme.palette.error.main, 0.4),
                      bg: alpha(theme.palette.error.main, 0.06),
                      hoverBg: alpha(theme.palette.error.main, 0.12),
                    };
                  case 'warning':
                    return {
                      icon: <FlagCircle fontSize="small" sx={{ color: 'warning.main' }} />,
                      textColor: 'warning.main',
                      borderColor: alpha(theme.palette.warning.main, 0.4),
                      bg: alpha(theme.palette.warning.main, 0.06),
                      hoverBg: alpha(theme.palette.warning.main, 0.12),
                    };
                  case 'debug':
                    return {
                      icon: <BugReport fontSize="small" sx={{ color: 'text.secondary' }} />,
                      textColor: 'text.secondary',
                      borderColor: alpha(theme.palette.text.secondary, 0.3),
                      bg: alpha(theme.palette.text.secondary, 0.05),
                      hoverBg: alpha(theme.palette.text.secondary, 0.1),
                    };
                  case 'info':
                  default:
                    return {
                      icon: <InfoIcon fontSize="small" sx={{ color: 'info.main' }} />,
                      textColor: 'info.main',
                      borderColor: alpha(theme.palette.info.main, 0.35),
                      bg: alpha(theme.palette.info.main, 0.05),
                      hoverBg: alpha(theme.palette.info.main, 0.1),
                    };
                }
              })();

              let payloadText: string | null = null;
              if (isExpanded && log.data) {
                if (typeof log.data === 'object') {
                  try {
                    payloadText = JSON.stringify(log.data, null, 2);
                  } catch {
                    payloadText = String(log.data);
                  }
                } else {
                  payloadText = String(log.data);
                }
              }

              return (
                <Box
                  key={log.id ?? logIndex}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: logLevelConfig.borderColor,
                    cursor: log.data ? 'pointer' : 'default',
                    bgcolor: isExpanded ? logLevelConfig.hoverBg : logLevelConfig.bg,
                    transition: 'background-color 0.2s ease',
                    '&:hover': log.data ? { bgcolor: logLevelConfig.hoverBg } : undefined,
                    contentVisibility: 'auto',
                    containIntrinsicSize: '64px',
                  }}
                  onClick={() => {
                    if (!log.data) return;
                    setExpandedLogId((prev) => (prev === logIdentifier ? null : logIdentifier));
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    {logLevelConfig.icon}
                    <Typography variant="caption" sx={{ color: logLevelConfig.textColor, fontWeight: 600 }}>
                      {level}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestampWithMicros(log.createdAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      • {log.event}
                    </Typography>
                  </Box>
                  <Typography variant="body2">{log.message}</Typography>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box
                      sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Typography
                        variant="body2"
                        component="pre"
                        sx={{
                          m: 0,
                          fontFamily: 'Monospace',
                          fontSize: 12,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}
                      >
                        {payloadText || '{}'}
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('components:callDetails.noLogs', 'Sem logs disponíveis')}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {t('components:callDetails.diagnosticsRestricted', 'Diagnostico disponivel apenas para administradores do sistema')}
    </Typography>
  );

  const handleExecuteScript = async () => {
    await executeScript();
  };
  const handleExecuteRefund = async () => {
    const executed = await executeRefund(refundReason);
    if (executed) {
      setRefundReason('');
    }
  };
  const shouldAutoAcceptOutboundSelf = (
    controlMode === 'outbound_self'
    && isSipOutboundSelfCall(call)
  );
  const shouldShowIncomingActions = controlMode === 'incoming'
    || (controlMode === 'outbound_self' && !shouldAutoAcceptOutboundSelf);
  const showActiveCallControlCard = showControlPanel && !isCallTerminalForControlPanel(call);
  const callControlCard = (
      <CallControlCard
        call={call}
        canControlMonitor={canControlMonitor}
        canControlHangup={canControlHangup}
        canControlHold={canControlHold}
        canControlMute={canControlHoldOrMute}
        showIncomingActions={shouldShowIncomingActions}
        twoWayMode={shouldAutoAcceptOutboundSelf ? 'on_mount' : 'disabled'}
        enableSipOrb={controlMode !== 'default'}
      />
  );

  useEffect(() => {
    setActiveTab(initialTab);
  }, [call.id, initialTab]);

  useEffect(() => {
    setRefundReason('');
  }, [call.id]);

  const generalContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight: 0,
      }}
    >
      {showActiveCallControlCard && callControlCard}
      {user?.isSuperuser === true && call.endedAt && <DiagnosticRecordingTracksViewer key={call.id} callId={call.id} />}

      {call.endedAt && (recordingSource || conversationSegments.length > 0) && (
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => setFullRecordingExpanded((prev) => !prev)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <GraphicEq fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={600}>
                {t('components:callDetails.fullCallRecording', 'Full call recording')}
              </Typography>
              <Tooltip title={t('components:callDetails.fullCallRecordingHelp', 'Full call timeline from pre-gate/ringing to call end.')} arrow>
                <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
            {fullRecordingExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Box>
          <Collapse in={fullRecordingExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, pb: 2 }}>
              {recordingSource ? (
                <WaveformPlayer
                  url={recordingSource}
                  displayName={`call-full-${call.id}.mp3`}
                  downloadFilename={`call-full-${call.id}.mp3`}
                  onTimeUpdate={(time) => setPlaybackTime(time)}
                  seekRequest={seekRequest}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('components:callDetails.recordingPending')}
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {conversationSegments.length > 0 && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <ButtonTabs
            tabs={conversationSegments.map((segment, index) => ({
              id: segment.id,
              label: (() => {
                const segmentMeta = getSegmentTabMeta(segment);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {segmentMeta.icon}
                    <Typography
                      variant="caption"
                      sx={{ textTransform: segment.segmentType === 'ai' ? 'none' : 'uppercase', fontWeight: 700 }}
                    >
                      {segmentMeta.title}
                    </Typography>
                    <Tooltip title={segmentMeta.tooltip} arrow>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  {index < conversationSegments.length - 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {'->'}
                    </Typography>
                  )}
                  {(((call.activeSegmentId && call.activeSegmentId === segment.id) || (call.activeSegmentType && call.activeSegmentType === segment.segmentType))
                    && !segment.endedAt) && (
                    <Tooltip title={t('components:callConsumption.active')} arrow>
                      <Box
                        data-testid={`segment-tab-active-dot-${segment.id}`}
                        aria-label={t('components:callConsumption.active')}
                        sx={{
                          position: 'relative',
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: 'success.main',
                          boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: -4,
                            borderRadius: '50%',
                            border: (theme) => `2px solid ${theme.palette.success.main}`,
                            opacity: 0.7,
                            animation: 'call-detail-segment-active-pulse 1.4s ease-out infinite',
                          },
                          '@keyframes call-detail-segment-active-pulse': {
                            '0%': { transform: 'scale(0.6)', opacity: 0.7 },
                            '70%': { transform: 'scale(1.2)', opacity: 0 },
                            '100%': { transform: 'scale(1.2)', opacity: 0 },
                          },
                        }}
                      />
                    </Tooltip>
                  )}
                  </Box>
                );
              })(),
            }))}
            value={selectedConversationSegmentId}
            onChange={(_, value) => setActiveConversationSegmentId(value)}
            renderContent={false}
            tabsSx={{ mt: 0, '& .MuiTabs-flexContainer': { gap: 1 } }}
          />
        </Box>
      )}

      {selectedSummaryText && (
        <Box
          sx={{
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => setSegmentSummaryExpanded((prev) => !prev)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <SummarizeIcon fontSize="small" color="primary" />
              <Typography variant="h6" fontWeight={600}>
                {t('components:callDetails.summary')}
              </Typography>
              {isAiSegment && (
                <Chip
                  size="small"
                  label={t('components:callDetails.segmentTabs.aiTitle', 'Agente de IA')}
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>
            {segmentSummaryExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Box>
          <Collapse in={segmentSummaryExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, pb: 2 }}>
              <Typography variant="body2">
                {selectedSummaryText}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )}

      {activeConversationSegment?.segmentType === 'ai' && canViewAiSegmentRecording && (
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                }}
                onClick={() => setSegmentRecordingExpanded((prev) => !prev)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <VoiceChatIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('components:callDetails.segmentRecording', 'Segment recording')}
                  </Typography>
                  {activeConversationSegment && (
                    <Chip
                      size="small"
                      label={getSegmentTabMeta(activeConversationSegment).title}
                      sx={{ fontWeight: 700 }}
                    />
                  )}
                  <Tooltip title={t('components:callDetails.segmentRecordingHelp', 'Audio attached to the currently selected segment only.')} arrow>
                    <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                  </Tooltip>
                </Box>
                {segmentRecordingExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </Box>
              <Collapse in={segmentRecordingExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 2 }}>
                  {selectedSegmentRecordingSource ? (
                    <WaveformPlayer
                      url={selectedSegmentRecordingSource}
                      displayName={`call-segment-${call.id}.mp3`}
                      downloadFilename={`call-segment-${call.id}.mp3`}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t('components:callDetails.segmentRecordingPending', 'Segment recording not available for this segment yet.')}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>
      )}

      {selectedTranscriptTurns.length > 0 && (
        <Box
          sx={{
            bgcolor: 'background.default',
            p: 2,
            borderRadius: 1,
            flex: 1,
            minHeight: 360,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
            <VoiceChatIcon fontSize="small" color="primary" />
            <Typography variant="h6" fontWeight={600}>
              {t('components:callDetails.fullTranscript')}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              py: 2,
              minHeight: 0,
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: 8,
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: scrollbarTrackColor,
                borderRadius: 999,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: scrollbarThumbColor,
                borderRadius: 999,
                border: `2px solid ${theme.palette.background.paper}`,
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: scrollbarThumbHoverColor,
              },
            }}
          >
            {selectedTranscriptTurns.map((turn, idx) => {
              const turnTime = selectedTranscriptTimes[idx];
              const nextTime = selectedTranscriptTimes[idx + 1];
              const isActive =
                playbackTime !== null &&
                turnTime !== null &&
                playbackTime >= turnTime &&
                (nextTime === null || nextTime === undefined || playbackTime < nextTime);
              return (
                <Box
                  key={idx}
                  sx={{
                    mb: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    cursor: turnTime !== null ? 'pointer' : 'default',
                    bgcolor: isActive ? 'action.selected' : 'transparent',
                    borderLeft: isActive ? '2px solid' : '2px solid transparent',
                    borderColor: isActive ? 'primary.main' : 'transparent',
                    contentVisibility: 'auto',
                    containIntrinsicSize: '56px',
                  }}
                  onClick={() => turnTime !== null && handleTranscriptSeek(turnTime)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color={turn.role === 'agent' ? 'primary.main' : 'text.secondary'}
                    >
                      {turn.role === 'agent' ? (
                        <>
                          <PsychologyIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {t('components:callDetails.agent')}
                        </>
                      ) : (
                        <>
                          <Person sx={{ fontSize: 14, mr: 0.5 }} />
                          {t('components:callDetails.user')}
                        </>
                      )}
                      :
                    </Typography>
                    {turnTime !== null && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {formatTranscriptTimestamp(turnTime)}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    {turn.message}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );

  const formatTimestamp = useCallback((value?: string) => {
    if (!value) return '-';
    return formatTimestampWithMicros(value);
  }, [formatTimestampWithMicros]);

  const isManualSipDirectCall = call.origin === 'manual' && call.callType === 'sip';
  const isManualAiAgentCall = call.origin === 'manual' && call.callType === 'ai_agent';
  const isManualReverseIvrCall = call.origin === 'manual' && call.callType === 'reverse_ivr';
  const isCampaignCall = call.origin === 'campaign';
  const isReverseIvrCall = call.callType === 'reverse_ivr';
  const shouldShowAgentField = Boolean(call.agentName) && !isManualSipDirectCall && !isReverseIvrCall;
  const hasAiAnswered = Boolean(
    (call.aiBilledSeconds || 0) > 0
      || (call.segments ?? []).some(
        (segment) =>
          segment.segmentType === 'ai'
          && Boolean(
            segment.connectedAt
            || (segment.durationSeconds || 0) > 0
            || (segment.billedSeconds || 0) > 0,
          ),
      ),
  );
  const hasHumanAnswered = Boolean(
    call.telephonyTransferConnectedAt
      || call.interactionAnsweredAt
      || call.interactionAnsweredById
      || call.interactionAnsweredExtension,
  );
  const transferWaitSeconds = (() => {
    if (call.telephonyTransferWaitMs !== undefined && call.telephonyTransferWaitMs !== null) {
      return Math.max(call.telephonyTransferWaitMs / 1000, 0);
    }
    if (call.telephonyTransferWaitStartedAt && call.telephonyTransferConnectedAt) {
      const waitStartedMs = Date.parse(call.telephonyTransferWaitStartedAt);
      const waitConnectedMs = Date.parse(call.telephonyTransferConnectedAt);
      if (Number.isFinite(waitStartedMs) && Number.isFinite(waitConnectedMs)) {
        return Math.max((waitConnectedMs - waitStartedMs) / 1000, 0);
      }
    }
    return null;
  })();
  const participantLabel = (() => {
    const participantName = (call.interactionAnsweredByName || call.interactionClaimedByName || '').trim();
    const participantExtension = (call.interactionAnsweredExtension || '').trim();
    if (!participantName && !participantExtension) return '-';
    return [participantName, participantExtension].filter(Boolean).join(' • ');
  })();
  const answeredParticipantName = (call.interactionAnsweredByName || '').trim() || '-';
  const answeredParticipantExtension = (call.interactionAnsweredExtension || '').trim() || '-';
  const originValue = (() => {
    if (isCampaignCall) {
      const campaignName = call.campaignName?.trim() || call.originLineName || '-';
      const contactDisplayName = call.campaignContactDisplayName?.trim();
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, textAlign: 'right' }}>
          <Typography variant="body2" fontWeight={600}>
            {campaignName}
          </Typography>
          {contactDisplayName && (
            <Typography variant="caption" color="text.secondary">
              {contactDisplayName}
            </Typography>
          )}
        </Box>
      );
    }

    if (isManualSipDirectCall) {
      const creatorName = call.createdByName?.trim();
      const creatorExtension = call.createdByExtension?.trim();
      if (!creatorName && !creatorExtension) return '-';
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, textAlign: 'right' }}>
          {creatorName && (
            <Typography variant="body2" fontWeight={600}>
              {creatorName}
            </Typography>
          )}
          {creatorExtension && (
            <Typography variant="caption" color="text.secondary">
              {creatorExtension}
            </Typography>
          )}
        </Box>
      );
    }

    if (isManualAiAgentCall || isManualReverseIvrCall) {
      return call.createdByName?.trim() || '-';
    }

    return call.originLineName || '-';
  })();
  const infoFields = [
    {
      label: t('components:callDetails.origin'),
      value: originValue,
    },
    ...(shouldShowAgentField
      ? [{
        label: t('components:callDetails.agent'),
        value: call.agentName || '-',
      }]
      : []),
    {
      label: t('components:callDetails.destination'),
      value: call.toNumber
        ? call.phoneLabel
          ? `${formatPhone(call.toNumber)} • ${call.phoneLabel}`
          : formatPhone(call.toNumber)
        : '-',
    },
    ...(destinationMetadataItems.length > 0
      ? [{
        label: t('components:callDetails.destinationMetadata', 'Metadados do destino'),
        value: (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: 0.75,
              maxWidth: { xs: '100%', md: 420 },
            }}
          >
            {destinationMetadataItems.map((item) => (
              <Box
                key={item}
                sx={{
                  px: 1,
                  py: 0.4,
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 600,
                    color: 'text.primary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        ),
      }]
      : []),
    {
      label: t('pages:calls.consumption'),
      value: (
        <Box sx={{ minWidth: 230, width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <CallConsumptionDisplay
            segments={call.consumptionSegments}
            segmentDetails={call.segments}
            activeSegmentId={call.activeSegmentId}
            activeSegmentType={call.activeSegmentType}
            callStatus={call.status}
            fallbackBilledSeconds={call.billedSeconds}
            fallbackDurationSeconds={call.duration}
            fallbackBucketType={call.callType === 'ai_agent' ? 'ai' : 'telephony'}
            compact
          />
        </Box>
      ),
    },
    {
      label: t('pages:calls.detail.ringDuration'),
      value: call.ringDuration !== undefined && call.ringDuration !== null
        ? formatDuration(call.ringDuration)
        : '-',
    },
    ...(isManualAiAgentCall
      ? [
        {
          label: t('components:callDetails.aiAnswered'),
          value: hasAiAnswered ? t('common:common.yes') : t('common:common.no'),
        },
        {
          label: t('components:callDetails.transferStarted'),
          value: (call.telephonyTransferStartedAt || call.transferDestinationType)
            ? t('common:common.yes')
            : t('common:common.no'),
        },
        {
          label: t('components:callDetails.humanWaitDuration'),
          value: transferWaitSeconds !== null ? formatDuration(transferWaitSeconds) : '-',
        },
        {
          label: t('components:callDetails.humanAnswered'),
          value: hasHumanAnswered ? t('common:common.yes') : t('common:common.no'),
        },
        {
          label: t('components:callDetails.participant'),
          value: participantLabel,
        },
      ]
      : []),
    ...(isManualReverseIvrCall
      ? [
        {
          label: t('components:callDetails.audioHeardDuration'),
          value: call.ivrAudioHeardSeconds !== undefined && call.ivrAudioHeardSeconds !== null
            ? formatDuration(call.ivrAudioHeardSeconds)
            : '-',
        },
        {
          label: t('components:callDetails.heardAudioToEnd'),
          value: call.ivrAudioHeardToEnd === true
            ? t('common:common.yes')
            : call.ivrAudioHeardToEnd === false
              ? t('common:common.no')
              : '-',
        },
        {
          label: t('components:callDetails.selectedDigitDtmf'),
          value: call.ivrSelectedDigit || '-',
        },
      ]
      : []),
    {
      label: t('components:callDetails.recordingAvailableLabel'),
      value: hasAnyRecording ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            variant="text"
            color="primary"
            onClick={() => setActiveTab('general')}
            sx={{ textTransform: 'none', minWidth: 0, px: 0.75, py: 0 }}
          >
            {t('common:common.yes')}
          </Button>
          <Tooltip title={t('components:callDetails.recordingAvailableTooltip')} arrow>
            <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
      ) : (
        <Typography variant="body2" fontWeight={400} color="text.secondary">
          {t('common:common.no')}
        </Typography>
      ),
    },
  ];

  const callVariableMap = call.variables ?? {};
  const fallbackVariables = contactVariables ?? {};
  const variableKeys = Array.from(
    new Set([...Object.keys(callVariableMap), ...Object.keys(fallbackVariables)]),
  )
    .filter((key) => {
      if (isSuperuser) return true;
      const normalizedKey = key.toLowerCase();
      return !(normalizedKey.startsWith('sys__') || normalizedKey.startsWith('system__'));
    })
    .sort((a, b) => a.localeCompare(b));
  const variableEntries = variableKeys
    .map((key) => ({
      key,
      value: callVariableMap[key] ?? fallbackVariables[key],
    }))
    .filter((entry) => entry.value !== undefined && entry.value !== null);

  const infoDateFields = [
    {
      label: t('pages:calls.detail.createdAt'),
      value: formatTimestamp(call.createdAt),
    },
    {
      label: t('pages:calls.detail.queuedAt'),
      value: formatTimestamp(call.queuedAt),
    },
    {
      label: t('pages:calls.detail.startedAt'),
      value: formatTimestamp(call.initiatedAt),
    },
    {
      label: t('pages:calls.detail.ringingAt'),
      value: formatTimestamp(call.ringingAt),
    },
    {
      label: t('pages:calls.detail.answeredAt'),
      value: formatTimestamp(call.answeredAt),
    },
    {
      label: t('pages:calls.detail.endedAt'),
      value: formatTimestamp(call.endedAt),
    },
  ];
  const isFinalizingCall = call.status === 'finalizing';
  const hasAmdMachineSegment = (call.segments ?? []).some((segment) => {
    if (segment.segmentType !== 'amd') return false;
    const analysis = asRecord(segment.analysis);
    const voicemail = asRecord(segment.voicemailDetection);
    const amdStatus = (
      asString(analysis?.asterisk_amd_status)
      || asString(analysis?.amd_status)
      || asString(analysis?.status)
      || asString(voicemail?.status)
      || asString(voicemail?.effective_status)
      || asString(asRecord(voicemail?.amd)?.status)
      || asString(asRecord(voicemail?.amd)?.effective_status)
      || ''
    ).toUpperCase();
    return amdStatus === 'MACHINE';
  });
  const isVoicemailDetectedByAmd = Boolean(
    call.voicemailDetection?.detected
      || hasAmdMachineSegment
      || call.status === 'voicemail',
  );
  const transferDestinationTypeLabel = useMemo(() => {
    const destinationType = call.transferDestinationType;
    if (!destinationType) return null;
    const labels: Record<string, string> = {
      internal_extension: t('components:callDetails.transfer.destinationTypes.internalExtension', 'Internal extension'),
      external_extension: t('components:callDetails.transfer.destinationTypes.externalExtension', 'External extension'),
      external_phone: t('components:callDetails.transfer.destinationTypes.externalPhone', 'External phone'),
    };
    return labels[destinationType] ?? destinationType;
  }, [call.transferDestinationType, t]);
  const logicalTransferDestinationLabel = useMemo(() => {
    if (!transferDestinationTypeLabel) return '-';
    const rawDestinationValue = (call.transferDestinationValue || '').trim();
    if (!rawDestinationValue) return transferDestinationTypeLabel;
    const destinationValue = call.transferDestinationType === 'external_phone'
      ? formatPhone(rawDestinationValue)
      : rawDestinationValue;
    return `${transferDestinationTypeLabel} • ${destinationValue}`;
  }, [call.transferDestinationType, call.transferDestinationValue, transferDestinationTypeLabel]);
  const resolvedTransferTargetLabel = useMemo(() => {
    const targetUserName = (call.transferTargetUserName || '').trim();
    const targetUserExtension = (call.transferTargetUserExtension || '').trim();
    if (targetUserName || targetUserExtension) {
      return [
        t('components:callDetails.transfer.resolvedKinds.user', 'User'),
        targetUserName,
        targetUserExtension,
      ].filter(Boolean).join(' • ');
    }

    const targetGroupName = (call.transferTargetGroupName || '').trim();
    if (targetGroupName) {
      return [
        t('components:callDetails.transfer.resolvedKinds.group', 'Group'),
        targetGroupName,
      ].filter(Boolean).join(' • ');
    }

    return '-';
  }, [
    call.transferTargetGroupName,
    call.transferTargetUserExtension,
    call.transferTargetUserName,
    t,
  ]);
  const transferSummaryRows = useMemo(
    () => [
      {
        label: t('components:callDetails.transfer.logicalDestination', 'Logical destination'),
        value: logicalTransferDestinationLabel,
      },
      {
        label: t('components:callDetails.transfer.resolvedTarget', 'Resolved target'),
        value: resolvedTransferTargetLabel,
      },
      {
        label: t('components:callDetails.transfer.answeredParticipant', 'Answered by'),
        value: answeredParticipantName,
      },
      {
        label: t('components:callDetails.transfer.participantExtension', 'Participant extension'),
        value: answeredParticipantExtension,
      },
      {
        label: t('components:callDetails.transfer.startedAt', 'Transfer started at'),
        value: formatTimestamp(call.telephonyTransferStartedAt ?? undefined),
      },
      {
        label: t('components:callDetails.transfer.waitStartedAt', 'Human wait started at'),
        value: formatTimestamp(call.telephonyTransferWaitStartedAt ?? undefined),
      },
      {
        label: t('components:callDetails.transfer.answeredAt', 'Human answered at'),
        value: formatTimestamp(call.interactionAnsweredAt || call.telephonyTransferConnectedAt || undefined),
      },
    ],
    [
      answeredParticipantExtension,
      answeredParticipantName,
      call.interactionAnsweredAt,
      call.telephonyTransferConnectedAt,
      call.telephonyTransferStartedAt,
      call.telephonyTransferWaitStartedAt,
      formatTimestamp,
      logicalTransferDestinationLabel,
      resolvedTransferTargetLabel,
      t,
    ],
  );
  const transferState = getTransferStateSnapshot(call);
  const transferStateLabel = useMemo(() => {
    if (!transferState) return null;
    if (transferState.key === 'connected') {
      return t('components:callDetails.transfer.state.connected', 'Connected');
    }
    if (transferState.key === 'unanswered') {
      return t('components:statusChips.call.labels.transfer_unanswered', 'Transfer unanswered');
    }
    if (transferState.key === 'ended') {
      return t('components:callDetails.transfer.state.ended', 'Ended');
    }
    if (transferState.key === 'inProgress') {
      return t('components:callDetails.transfer.state.inProgress', 'In progress');
    }
    return t('components:callDetails.transfer.state.pending', 'Pending');
  }, [transferState, t]);
  const isExternalPhoneTransfer = call.transferDestinationType === 'external_phone';
  const transferHint = useMemo(() => {
    if (transferState?.isHistorical) {
      return t(
        'components:callDetails.transfer.historicalHint',
        'This call is already ended. This box only shows the recorded transfer destination and state.',
      );
    }
    return isExternalPhoneTransfer
      ? t(
        'components:callDetails.transfer.externalPhoneHint',
        'This destination cannot be answered in the webphone. Use this panel to follow call context and diagnostics.',
      )
      : t(
        'components:callDetails.transfer.extensionHint',
        'This destination is extension-based. Monitor and call controls remain available in this panel.',
      );
  }, [isExternalPhoneTransfer, t, transferState?.isHistorical]);
  const reverseIvrAudioContent = isReverseIvrCall ? (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        p: 2,
      }}
    >
      <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GraphicEq fontSize="small" color="primary" />
        {t('components:callDetails.reverseIvrAudio')}
      </Typography>
      <Box sx={{ mt: 2 }}>
        {call.ivrAudioFileUrl ? (
          <WaveformPlayer
            url={call.ivrAudioFileUrl}
            displayName={`call-ivr-${call.id}.mp3`}
            downloadFilename={`call-ivr-${call.id}.mp3`}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('components:callDetails.reverseIvrAudioPending')}
          </Typography>
        )}
      </Box>
    </Box>
  ) : null;
  const infoContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {isFinalizingCall && (
        <Alert severity="info" variant="outlined">
          <Typography variant="subtitle2" fontWeight={600}>
            {t('components:callDetails.alerts.finalizing.title')}
          </Typography>
          <Typography variant="body2">
            {t('components:callDetails.alerts.finalizing.message')}
          </Typography>
        </Alert>
      )}
      {isVoicemailDetectedByAmd && (
        <Alert severity="warning" variant="outlined" icon={<Voicemail fontSize="small" />}>
          <Typography variant="subtitle2" fontWeight={600}>
            {t('components:callDetails.alerts.voicemail.title')}
          </Typography>
          <Typography variant="body2">
            {t('components:callDetails.alerts.voicemail.message')}
          </Typography>
          {call.voicemailDetection?.reason && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {call.voicemailDetection.reason}
            </Typography>
          )}
          {call.voicemailDetection?.voicemail_message && (
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              {call.voicemailDetection.voicemail_message}
            </Typography>
          )}
        </Alert>
      )}
      {call.transferDestinationType && (
        <Alert severity="info" variant="outlined" icon={<VoiceChatIcon fontSize="small" />}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t('components:callDetails.transfer.title', 'Telephony transfer')}
              </Typography>
              {transferStateLabel && (
                <Chip
                  size="small"
                  color={transferState?.chipColor ?? 'default'}
                  label={transferStateLabel}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {transferSummaryRows.map((entry) => (
                <Box key={entry.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {entry.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right' }}>
                    {entry.value}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary">
              {transferHint}
            </Typography>
          </Box>
        </Alert>
      )}
      {hasIvrSummary && (
        <Alert severity="info" variant="outlined" icon={<Dialpad fontSize="small" />}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t('components:callDetails.ivr.title', 'IVR resolution')}
            </Typography>
            <Typography variant="body2">
              {t('components:callDetails.ivr.selectedDigit', 'Selected digit')}: {ivrSelectedDigit}
            </Typography>
            <Typography variant="body2">
              {t('components:callDetails.ivr.selectedStatus', 'Mapped status')}: {formatIvrStatusLabel(ivrSelectedStatus)}
            </Typography>
            <Typography variant="body2">
              {t('components:callDetails.ivr.resolution', 'Final IVR resolution')}: {ivrResolution ? toTitleCase(ivrResolution) : '-'}
            </Typography>
            {latestIvrSegment?.ivrDtmfReceivedAt && (
              <Typography variant="body2">
                {t('components:callDetails.ivr.receivedAt', 'Digit (DTMF) received at')}: {formatTimestamp(latestIvrSegment.ivrDtmfReceivedAt)}
              </Typography>
            )}
            {latestIvrSegment?.ivrEndReason && (
              <Typography variant="body2">
                {t('components:callDetails.ivr.endReason', 'IVR end reason')}: {toTitleCase(latestIvrSegment.ivrEndReason)}
              </Typography>
            )}
          </Box>
        </Alert>
      )}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          p: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon fontSize="small" color="primary" />
          {t('components:callDetails.callDetails')}
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {infoFields.map((entry) => (
            <Box key={entry.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {entry.label}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {typeof entry.value === 'string' || typeof entry.value === 'number' ? (
                  <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right' }}>
                    {entry.value}
                  </Typography>
                ) : (
                  entry.value
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {reverseIvrAudioContent}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          p: 2,
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlagCircle fontSize="small" color="primary" />
          {t('pages:contacts.status')}
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {infoDateFields.map((entry) => (
            <Box key={entry.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {entry.label}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right' }}>
                {entry.value}
              </Typography>
            </Box>
          ))}
        </Box>
        {((call.terminationReasonCode || call.terminationReasonText) || call.errorMessage) && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {(call.terminationReasonCode || call.terminationReasonText) && (
              <Typography variant="body2">
                <strong>{t('components:callDetails.terminationReason')}: </strong> {terminationReasonDisplay}
              </Typography>
            )}
            {call.errorMessage && (
              <Typography variant="body2" color="error.main">
                <strong>{t('components:callDetails.error')}</strong> {call.errorMessage}
              </Typography>
            )}
          </Box>
        )}
        {billingOverageLog && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
            {t('components:callDetails.billingOverageNotice', {
              value: billingOverageSeconds !== null ? formatDuration(billingOverageSeconds) : '-',
            })}
          </Alert>
        )}
      </Box>
    </Box>
  );

  const placeholderContent = (
    <Box
      sx={{
        height: '100%',
        bgcolor: 'background.default',
        borderRadius: 1,
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('components:callDetails.tabs.empty')}
      </Typography>
    </Box>
  );

  const variablesContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('components:callDetails.variablesObservation')}
      </Typography>
      {variableEntries.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('components:callDetails.noVariables')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            pr: 1,
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 2,
              }}
            >
              {variableEntries.map(({ key, value }) => (
                <Box key={key}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                    {key}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {String(value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  const dataContent = hasDataEntries ? (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('components:callDetails.dataCollectedObservation')}
      </Typography>
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pr: 1,
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 2,
            }}
          >
            {dataEntries.map(({ key, data }) => {
              const dataRecord = asRecord(data);
              const entryValue = dataRecord && Object.prototype.hasOwnProperty.call(dataRecord, 'value')
                ? dataRecord.value
                : data;
              const entryRationale = dataRecord
                ? asString(dataRecord.rationale)
                : null;

              return (
                <Box key={key}>
                  <Chip
                    label={toTitleCase(key)}
                    size="small"
                    sx={{
                      mt: 0.75,
                      borderRadius: 1,
                      bgcolor: 'primary.dark',
                      color: 'primary.contrastText',
                      fontWeight: 600,
                      width: '100%',
                      justifyContent: 'flex-start',
                      px: 1.5,
                      py: 0.5,
                    }}
                  />
                  <Typography variant="h6" color="text.secondary" sx={{ textTransform: 'capitalize', p: 1 }}>
                    {String(entryValue)}
                  </Typography>

                  {entryRationale && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', p: 1 }}>
                      {entryRationale}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  ) : (
    placeholderContent
  );

  const refundsContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {refundsLoading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('components:callDetails.refunds.description')}
            </Typography>
            {refundsError ? (
              <Alert severity="error" variant="outlined">
                {refundsError}
              </Alert>
            ) : refundsData ? (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                    gap: 1,
                  }}
                >
                  {[
                    {
                      label: t('components:callDetails.refunds.summary.gross', 'Bruto IA'),
                      value: formatDuration(refundsData.ai.grossBilledSeconds || 0),
                    },
                    {
                      label: t('components:callDetails.refunds.summary.net', 'Liquido IA'),
                      value: formatDuration(refundsData.ai.netBilledSeconds || 0),
                    },
                    {
                      label: t('components:callDetails.refunds.summary.refunded', 'Ja estornado'),
                      value: formatDuration(refundsData.ai.refundedSeconds || 0),
                    },
                    {
                      label: t('components:callDetails.refunds.summary.refundable', 'Saldo estornavel'),
                      value: formatDuration(refundsData.ai.refundableSeconds || 0),
                    },
                  ].map((item) => (
                    <Box
                      key={item.label}
                      sx={{
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 1.5,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {item.label}
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                <Typography variant="body2" color="text.secondary">
                  <strong>{t('components:callDetails.refunds.lastRefundedAt', 'Ultimo estorno')}:</strong>{' '}
                  {refundsData.ai.lastRefundedAt ? formatDateTime(refundsData.ai.lastRefundedAt, { locale }) : '-'}
                </Typography>

                {!refundsData.callFinalized && (
                  <Alert severity="warning" variant="outlined">
                    {t('components:callDetails.refunds.blocked.callNotFinalized')}
                  </Alert>
                )}
                {refundsData.callFinalized && !refundsData.ai.canRefund && (
                  <Alert severity="info" variant="outlined">
                    {t('components:callDetails.refunds.blocked.noRefundableBalance')}
                  </Alert>
                )}

                {refundExecuteError && (
                  <Alert severity="error" variant="outlined">
                    {refundExecuteError}
                  </Alert>
                )}
                {refundExecuteState === 'success' && (
                  <Alert severity="success" variant="outlined">
                    {t('components:callDetails.refunds.executeSuccess')}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  size="small"
                  label={t('components:callDetails.refunds.reasonLabel')}
                  value={refundReason}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => setRefundReason(event.target.value)}
                  placeholder={t('components:callDetails.refunds.reasonPlaceholder')}
                  disabled={!refundsData.ai.canRefund || refundExecuteState === 'sending'}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={refundExecuteState === 'sending' ? <CircularProgress size={14} color="inherit" /> : <RestartAlt />}
                    onClick={handleExecuteRefund}
                    disabled={!refundsData.ai.canRefund || refundExecuteState === 'sending'}
                  >
                    {refundExecuteState === 'sending'
                      ? t('components:callDetails.refunds.executeSending')
                      : t('components:callDetails.refunds.executeButton')}
                  </Button>
                </Box>
              </>
            ) : null}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {refundsData && refundsData.history.length === 0 ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('components:callDetails.refunds.noHistory')}
                </Typography>
              </Box>
            ) : (
              refundsData?.history.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.warning.main, 0.35),
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <RestartAlt fontSize="small" sx={{ color: 'warning.main' }} />
                    <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
                      {formatDuration(entry.appliedSeconds || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(entry.createdAt, { locale })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      • {entry.event}
                    </Typography>
                  </Box>
                  <Typography variant="body2">{entry.message}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('components:callDetails.refunds.history.requestedLabel', 'Solicitado')}: {formatDuration(entry.requestedSeconds || 0)}
                    {' • '}
                    {t('components:callDetails.refunds.history.appliedLabel', 'Aplicado')}: {formatDuration(entry.appliedSeconds || 0)}
                    {' • '}
                    {t('components:callDetails.refunds.history.unallocatedLabel', 'Nao alocado')}: {formatDuration(entry.unallocatedSeconds || 0)}
                  </Typography>
                  {(entry.refundReason || entry.refundReasonSource || entry.actorUserDisplayName) && (
                    <Typography variant="caption" color="text.secondary">
                      {entry.refundReason ? `${t('components:callDetails.refunds.history.reasonLabel', 'Motivo')}: ${entry.refundReason}` : ''}
                      {entry.refundReason && (entry.refundReasonSource || entry.actorUserDisplayName) ? ' • ' : ''}
                      {entry.refundReasonSource ? `${t('components:callDetails.refunds.history.sourceLabel', 'Origem')}: ${entry.refundReasonSource}` : ''}
                      {entry.refundReasonSource && entry.actorUserDisplayName ? ' • ' : ''}
                      {entry.actorUserDisplayName ? `${t('components:callDetails.refunds.history.actorLabel', 'Operador')}: ${entry.actorUserDisplayName}` : ''}
                    </Typography>
                  )}
                </Box>
              ))
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const scriptsContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {scriptsLoading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('components:callDetails.scripts.description')}
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              label={t('components:callDetails.scripts.scriptLabel')}
              value={selectedScriptId}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setSelectedScriptId(event.target.value);
              }}
              disabled={callScripts.length === 0 || !canExecuteScripts}
            >
              {callScripts.map((script) => (
                <MenuItem key={script.id} value={script.id}>
                  {script.name}
                </MenuItem>
              ))}
            </TextField>
            {!canExecuteScripts && (
              <Typography variant="body2" color="text.secondary">
                {t('components:callDetails.scripts.permissionHint')}
              </Typography>
            )}
            {executeError && (
              <Typography variant="body2" color="error">
                {executeError}
              </Typography>
            )}
            {executeState === 'success' && (
              <Typography variant="body2" color="success.main">
                {t('components:callDetails.scripts.executeSuccess')}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={executeState === 'sending' ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
                onClick={handleExecuteScript}
                disabled={!canExecuteScripts || !selectedScriptId || executeState === 'sending'}
              >
                {executeState === 'sending'
                  ? t('components:callDetails.scripts.executeSending')
                  : t('components:callDetails.scripts.executeButton')}
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              pr: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {scriptsError && (
              <Alert severity="error" variant="outlined">
                {scriptsError}
              </Alert>
            )}
            {!scriptsError && scriptExecutions.length === 0 && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('components:callDetails.scripts.noExecutions')}
                </Typography>
              </Box>
            )}
            {!scriptsError && scriptExecutions.map((execution) => (
              (() => {
                const executionIdentifier = execution.id;
                const isExpanded = expandedExecutionId === executionIdentifier;
                const hasDetails = Boolean(execution.output || execution.error);
                const status = execution.status || 'failed';
                const statusConfig = (() => {
                  switch (status) {
                    case 'completed':
                      return {
                        icon: <CheckCircleOutline fontSize="small" sx={{ color: 'success.main' }} />,
                        textColor: 'success.main',
                        borderColor: alpha(theme.palette.success.main, 0.35),
                        bg: alpha(theme.palette.success.main, 0.05),
                        hoverBg: alpha(theme.palette.success.main, 0.1),
                      };
                    case 'running':
                      return {
                        icon: <ScheduleIcon fontSize="small" sx={{ color: 'info.main' }} />,
                        textColor: 'info.main',
                        borderColor: alpha(theme.palette.info.main, 0.35),
                        bg: alpha(theme.palette.info.main, 0.05),
                        hoverBg: alpha(theme.palette.info.main, 0.1),
                      };
                    case 'pending':
                      return {
                        icon: <PendingIcon fontSize="small" sx={{ color: 'warning.main' }} />,
                        textColor: 'warning.main',
                        borderColor: alpha(theme.palette.warning.main, 0.35),
                        bg: alpha(theme.palette.warning.main, 0.05),
                        hoverBg: alpha(theme.palette.warning.main, 0.1),
                      };
                    case 'timeout':
                    case 'failed':
                    default:
                      return {
                        icon: <ErrorOutline fontSize="small" sx={{ color: 'error.main' }} />,
                        textColor: 'error.main',
                        borderColor: alpha(theme.palette.error.main, 0.4),
                        bg: alpha(theme.palette.error.main, 0.06),
                        hoverBg: alpha(theme.palette.error.main, 0.12),
                      };
                  }
                })();

                const statusLabel = t(
                  `components:callDetails.scripts.statuses.${status}`,
                  status,
                );

                return (
                  <Box
                    key={execution.id}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: statusConfig.borderColor,
                      cursor: hasDetails ? 'pointer' : 'default',
                      bgcolor: isExpanded ? statusConfig.hoverBg : statusConfig.bg,
                      transition: 'background-color 0.2s ease',
                      '&:hover': hasDetails ? { bgcolor: statusConfig.hoverBg } : undefined,
                    }}
                    onClick={() => {
                      if (!hasDetails) return;
                      setExpandedExecutionId((prev) => (prev === executionIdentifier ? null : executionIdentifier));
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      {statusConfig.icon}
                      <Typography variant="caption" sx={{ color: statusConfig.textColor, fontWeight: 600 }}>
                        {statusLabel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(execution.createdAt, { locale })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        • {execution.scriptName}
                      </Typography>
                    </Box>
                    {typeof execution.executionTimeMs === 'number' && (
                      <Typography variant="body2" color="text.secondary">
                        {t('components:callDetails.scripts.executionTime')}: {formatNumber(execution.executionTimeMs)}ms
                      </Typography>
                    )}
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box
                        sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {execution.output && (
                          <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                              m: 0,
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'Monospace',
                              fontSize: 12,
                              wordBreak: 'break-word',
                            }}
                          >
                            {execution.output}
                          </Typography>
                        )}
                        {execution.output && execution.error && (
                          <Box sx={{ my: 1, borderTop: '1px solid', borderColor: 'divider' }} />
                        )}
                        {execution.error && (
                          <Typography
                            variant="body2"
                            component="pre"
                            color="error.main"
                            sx={{
                              m: 0,
                              whiteSpace: 'pre-wrap',
                              fontFamily: 'Monospace',
                              fontSize: 12,
                              wordBreak: 'break-word',
                            }}
                          >
                            {execution.error}
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })()
            ))}
          </Box>
        </>
      )}
    </Box>
  );

  const tabItems: ButtonTab[] = [
    { id: 'information', label: t('components:callDetails.tabs.information'), icon: <InfoIcon fontSize="small" />, content: infoContent },
    { id: 'general', label: t('components:callDetails.tabs.conversation'), icon: <VoiceChatIcon fontSize="small" />, content: generalContent },
    { id: 'variables', label: t('components:callDetails.tabs.variables'), icon: <DataObject fontSize="small" />, content: variablesContent },
    { id: 'dataCollected', label: t('components:callDetails.tabs.dataCollected'), icon: <AnalyticsIcon fontSize="small" />, content: dataContent },
    { id: 'scripts', label: t('components:callDetails.tabs.scripts'), icon: <Code fontSize="small" />, content: scriptsContent },
  ];

  if (canViewRefunds) {
    tabItems.push({
      id: 'refunds',
      label: t('components:callDetails.tabs.refunds', 'Estornos'),
      icon: <RestartAlt fontSize="small" />,
      content: refundsContent,
    });
  }

  if (canViewDiagnostics) {
    tabItems.push({
      id: 'diagnostics',
      label: t('components:callDetails.tabs.diagnostics', 'Diagnostico'),
      icon: <FlagCircle fontSize="small" />,
      content: diagnosticsContent,
    });
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <ButtonTabs
        tabs={tabItems}
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
        contentSx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}
      />
    </Box>
  );
}
